import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  X, Building2, FileText, Search, Upload, ArrowRight, ArrowLeft,
  Calendar, CheckCircle2, Circle, Loader2, Plus, Trash2, RotateCcw,
  Download, FileSearch, Sparkles, Database,
} from 'lucide-react';
import Card, { CardBody } from './ui/Card';
import Button from './ui/Button';
import { toast } from 'sonner';
import { standaloneCompaniesAPI, indiaCompanyAPI, ukCompanyAPI } from '../lib/api';
import PdfStatementsPanel from './financial/PdfStatementsPanel';
import { useLiveCountriesStore, type CountryKey } from '../stores/liveCountriesStore';

// ─── Types ───────────────────────────────────────────────────────────────────

type Country = 'us' | 'india' | 'uk' | null;
type UKTimeRange = '1year' | '2years' | '3years' | '5years' | '10years';
type IndiaResearchMode = 'search' | 'pdf' | null;
type ResearchMode = 'sec' | 'pdf' | null;
type TimeRange = '1year' | '2years' | '3years' | '5years';
type DataType = 'consolidated' | 'standalone';

interface IndiaQueueItem {
  id: string;
  companyName: string;
  dataType: DataType;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  jobId?: string;
  message?: string;
}

interface UKQueueItem {
  id: string;
  companyName: string;       // Display name (Companies House official, if picked)
  companyNumber?: string;    // 8-digit CH number, optional (resolved server-side if absent)
  timeRange: UKTimeRange;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message?: string;
  companyId?: string;
}

interface USQueueItem {
  id: string;
  companyName: string;
  ticker?: string;
  cik?: string;
  timeRange: TimeRange;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message?: string;
  companyId?: string;
}

interface ResearchCompanyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onResearchComplete?: () => void;
  /** When true, renders as a full page instead of a modal overlay. */
  isPage?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ResearchCompanyDialog({
  isOpen,
  onClose,
  onResearchComplete,
  isPage = false,
}: ResearchCompanyDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isCountryLive = useLiveCountriesStore((s) => s.isLive);

  /**
   * Leave the research flow for a company page or the list.
   *
   * ALWAYS invalidate through here rather than calling `navigate` directly:
   * the companies list is session-cached (staleTime: Infinity,
   * refetchOnMount: false), so landing on it reuses the cached rows and a
   * newly-researched company stays invisible until a hard reload. The list's
   * pending-job poller cannot rescue that either — it only polls when a row
   * with a pending status is already in the cache, and a brand-new company has
   * no row at all.
   *
   * Every research path is asynchronous, so this is called on job COMPLETION,
   * never straight after the POST that enqueues the job.
   */
  const navigateAfterResearch = useCallback(
    (to: string, delayMs = 0) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['financial'] });
      if (delayMs > 0) setTimeout(() => navigate(to), delayMs);
      else navigate(to);
    },
    [navigate, queryClient]
  );

  // ── Shared ────────────────────────────────────────────────────────────────
  const [selectedCountry, setSelectedCountry] = useState<Country>(null);
  const [existingCompanies, setExistingCompanies] = useState<any[]>([]);
  const [selectedExistingCompany, setSelectedExistingCompany] = useState<any | null>(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [companySelectionMode, setCompanySelectionMode] = useState<'new' | 'existing'>('new');

  // ── India flow ────────────────────────────────────────────────────────────
  type IndiaSearchItem = { stock_name: string; sc_id?: string; sc_nse_symbol?: string; sc_bse_symbol?: string; [key: string]: any };
  const [indiaResearchMode, setIndiaResearchMode] = useState<IndiaResearchMode>(null);
  const [indiaQueue, setIndiaQueue] = useState<IndiaQueueItem[]>([]);
  const [indiaInputName, setIndiaInputName] = useState('');
  const [indiaInputTicker, setIndiaInputTicker] = useState(''); // NSE/BSE symbol auto-filled on pick
  const [indiaInputDataType, setIndiaInputDataType] = useState<DataType>('consolidated');
  const [mcSearchResults, setMcSearchResults] = useState<IndiaSearchItem[]>([]);
  const [mcSearchLoading, setMcSearchLoading] = useState(false);
  const [mcSearchError, setMcSearchError] = useState<string | null>(null);
  // Inline autocomplete dropdown control (mirrors UK).
  const [indiaSearchOpen, setIndiaSearchOpen] = useState(false);
  const [indiaSearchSuppressed, setIndiaSearchSuppressed] = useState(false);
  // Elapsed-time ticker for the India extraction loader.
  const [indiaElapsedSec, setIndiaElapsedSec] = useState(0);
  const indiaStartTimeRef = useRef<number | null>(null);
  const onCloseRef = useRef(onClose); // stable ref so Escape handler doesn't re-register
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // ── UK flow ───────────────────────────────────────────────────────────────
  // Companies House search result shape used everywhere in the UK flow.
  type UKSearchItem = {
    company_name: string;
    company_number: string;
    company_status?: string | null;
    company_type?: string | null;
    date_of_creation?: string | null;
    address_snippet?: string | null;
  };
  const [ukCompanyName, setUkCompanyName] = useState('');
  const [ukCompanyNumber, setUkCompanyNumber] = useState('');
  const [ukSearchResults, setUkSearchResults] = useState<UKSearchItem[]>([]);
  const [ukSearchLoading, setUkSearchLoading] = useState(false);
  // Open state for the inline autocomplete dropdown beneath the Name input.
  const [ukSearchOpen, setUkSearchOpen] = useState(false);
  // True after the user has picked from the dropdown so we don't keep re-searching
  // when the name input gets repopulated with the official Companies House name.
  const [ukSearchSuppressed, setUkSearchSuppressed] = useState(false);
  const [ukTimeRange, setUkTimeRange] = useState<UKTimeRange>('5years');
  // Elapsed-time ticker for the rich extraction loader. Starts the first time
  // any UK queue item enters `processing`, resets when none are processing.
  const [ukElapsedSec, setUkElapsedSec] = useState(0);
  const ukStartTimeRef = useRef<number | null>(null);
  // Queue of UK companies to research. Mirrors `indiaQueue` exactly so the
  // UX is consistent across countries — add many companies, then kick off
  // research on the whole list.
  const [ukQueue, setUkQueue] = useState<UKQueueItem[]>([]);
  const [ukAvailability, setUkAvailability] = useState<{
    status: 'success' | 'not_found' | 'error';
    company_number: string | null;
    company_name: string | null;
    available_years: number[];
    filing_count: number;
    message: string;
  } | null>(null);
  const [ukAvailabilityLoading, setUkAvailabilityLoading] = useState(false);

  // ── Guard refs ────────────────────────────────────────────────────────────
  const pollIntervalMapRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  // ── US flow ───────────────────────────────────────────────────────────────
  type USSearchItem = { companyName: string; ticker: string; cik: string; exchange?: string };
  const [researchMode, setResearchMode] = useState<ResearchMode>(null);
  // Single-company form (SEC mode) — name + ticker auto-filled from autocomplete.
  const [usCompanyName, setUsCompanyName] = useState('');
  const [usCompanyTicker, setUsCompanyTicker] = useState('');
  const [usCompanyCik, setUsCompanyCik] = useState('');
  const [searchResults, setSearchResults] = useState<USSearchItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [usSearchOpen, setUsSearchOpen] = useState(false);
  const [usSearchSuppressed, setUsSearchSuppressed] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>('2years');
  // Queue of US companies to research. Mirrors UK/India.
  const [usQueue, setUsQueue] = useState<USQueueItem[]>([]);
  // Elapsed-time ticker for the US extraction loader.
  const [usElapsedSec, setUsElapsedSec] = useState(0);
  const usStartTimeRef = useRef<number | null>(null);
  // PDF mode renders <PdfStatementsPanel>, which owns all its own state. While
  // its extraction job is in flight we lock the dialog's escape hatches
  // (Escape / backdrop / header close / country Change) — closing would lose
  // the only UI tracking the job. Ref mirrors the state so the Escape handler
  // doesn't need to re-register.
  const [isPdfPanelRunning, setIsPdfPanelRunning] = useState(false);
  const isPdfPanelRunningRef = useRef(false);
  const handlePdfRunningChange = useCallback((running: boolean) => {
    isPdfPanelRunningRef.current = running;
    setIsPdfPanelRunning(running);
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  const isIndiaProcessing = indiaQueue.some(item => item.status === 'processing');
  const hasPendingInQueue = indiaQueue.some(item => item.status === 'pending');
  // Re-derived so the rest of the file (which references ukIsProcessing on
  // many controls) keeps working without a `useState` setter. True while any
  // UK queue item is mid-flight.
  const ukIsProcessing = ukQueue.some(item => item.status === 'processing');
  const hasPendingInUKQueue = ukQueue.some(item => item.status === 'pending');
  // US — same shape as UK/India.
  const isUSProcessing = usQueue.some(item => item.status === 'processing');
  const hasPendingInUSQueue = usQueue.some(item => item.status === 'pending');
  // Legacy alias kept so backdrop / Escape handler / shared modal code
  // (which referenced `isPollingUS`) doesn't need rewiring.
  const isPollingUS = isUSProcessing;

  // ── Multi-company queue completion → navigate to /companies ───────────────
  // Refs track whether each queue ever entered processing state. When it
  // transitions from "any processing" back to "none processing" AND the queue
  // had multiple items, we've finished a multi-company run — redirect to the
  // companies page so the user can see the freshly-loaded set.
  const indiaWasProcessingRef = useRef(false);
  const ukWasProcessingRef = useRef(false);
  const usWasProcessingRef = useRef(false);

  useEffect(() => {
    if (isIndiaProcessing) { indiaWasProcessingRef.current = true; return; }
    if (indiaWasProcessingRef.current && indiaQueue.length >= 2) {
      indiaWasProcessingRef.current = false;
      onResearchComplete?.();
      navigateAfterResearch('/companies', 1500);
    } else if (!isIndiaProcessing) {
      indiaWasProcessingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIndiaProcessing, indiaQueue.length]);

  useEffect(() => {
    if (ukIsProcessing) { ukWasProcessingRef.current = true; return; }
    if (ukWasProcessingRef.current && ukQueue.length >= 2) {
      ukWasProcessingRef.current = false;
      onResearchComplete?.();
      navigateAfterResearch('/companies', 1500);
    } else if (!ukIsProcessing) {
      ukWasProcessingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ukIsProcessing, ukQueue.length]);

  useEffect(() => {
    if (isUSProcessing) { usWasProcessingRef.current = true; return; }
    if (usWasProcessingRef.current && usQueue.length >= 2) {
      usWasProcessingRef.current = false;
      onResearchComplete?.();
      navigateAfterResearch('/companies', 1500);
    } else if (!isUSProcessing) {
      usWasProcessingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUSProcessing, usQueue.length]);

  // ── Reset on close ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      // clear India
      setSelectedCountry(null);
      setIndiaResearchMode(null);
      setIndiaQueue([]);
      setIndiaInputName('');
      setIndiaInputTicker('');
      setIndiaInputDataType('consolidated');
      setMcSearchResults([]);
      setMcSearchError(null);
      setIndiaSearchOpen(false);
      setIndiaSearchSuppressed(false);
      setIndiaElapsedSec(0);
      indiaStartTimeRef.current = null;
      // clear UK
      setUkCompanyName('');
      setUkCompanyNumber('');
      setUkSearchResults([]);
      setUkSearchLoading(false);
      setUkSearchOpen(false);
      setUkSearchSuppressed(false);
      setUkTimeRange('5years');
      setUkQueue([]);
      setUkAvailability(null);
      setUkAvailabilityLoading(false);
      setUkElapsedSec(0);
      ukStartTimeRef.current = null;
      // clear US
      setResearchMode(null);
      setUsCompanyName('');
      setUsCompanyTicker('');
      setUsCompanyCik('');
      setSearchResults([]);
      setUsSearchOpen(false);
      setUsSearchSuppressed(false);
      setTimeRange('2years');
      setUsQueue([]);
      setUsElapsedSec(0);
      usStartTimeRef.current = null;
      setIsPdfPanelRunning(false);
      isPdfPanelRunningRef.current = false;
      // shared
      setSelectedExistingCompany(null);
      setCompanySelectionMode('new');
    }
  }, [isOpen]);

  // ── Body scroll lock & Escape ─────────────────────────────────────────────
  useEffect(() => {
    if (isPage) return; // page mode — don't lock scroll
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen, isPage]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Escape closes — background pipelines continue server-side whether or
      // not the dialog is open. Exception: a running PDF-statements job, whose
      // only progress UI lives inside this dialog.
      if (e.key === 'Escape' && isOpen && !isPage && !isPdfPanelRunningRef.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isPage]);

  // ── Cleanup all intervals on unmount ──────────────────────────────────────
  useEffect(() => {
    return () => {
      pollIntervalMapRef.current.forEach(interval => clearInterval(interval));
      pollIntervalMapRef.current.clear();
    };
  }, []);

  // ── Load existing companies when country is selected ──────────────────────
  useEffect(() => {
    if (!selectedCountry) return;

    const controller = new AbortController();
    setIsLoadingCompanies(true);
    standaloneCompaniesAPI.listCompanies(controller.signal)
      .then((companies: any[]) => { if (!controller.signal.aborted) setExistingCompanies(companies); })
      .catch(() => { if (!controller.signal.aborted) toast.error('Failed to load existing companies'); })
      .finally(() => { if (!controller.signal.aborted) setIsLoadingCompanies(false); });

    return () => controller.abort();
  }, [selectedCountry]);

  // ── Debounced India autocomplete ──────────────────────────────────────────
  // Mirrors UK pattern: types >= 3 chars, results populate the inline dropdown,
  // suppressed once the user picks a result.
  useEffect(() => {
    if (selectedCountry !== 'india') return;
    if (indiaSearchSuppressed) return;
    if (isIndiaProcessing) return;

    const q = indiaInputName.trim();
    if (q.length < 3) {
      setMcSearchResults([]);
      setIndiaSearchOpen(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setMcSearchLoading(true);
      setMcSearchError(null);
      try {
        const res = await indiaCompanyAPI.searchCompanies(q);
        if (cancelled) return;
        const results = (res.results || []) as IndiaSearchItem[];
        setMcSearchResults(results);
        setIndiaSearchOpen(true);
        if (results.length === 0) setMcSearchError('No companies found. Try a different name.');
      } catch {
        if (!cancelled) {
          setMcSearchError('Search failed. Please try again.');
          setMcSearchResults([]);
          setIndiaSearchOpen(false);
        }
      } finally {
        if (!cancelled) setMcSearchLoading(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indiaInputName, selectedCountry, indiaSearchSuppressed, isIndiaProcessing]);

  // ── Elapsed-time ticker for the India extraction loader ───────────────────
  useEffect(() => {
    if (!isIndiaProcessing) {
      setIndiaElapsedSec(0);
      indiaStartTimeRef.current = null;
      return;
    }
    if (indiaStartTimeRef.current === null) indiaStartTimeRef.current = Date.now();
    const interval = setInterval(() => {
      if (indiaStartTimeRef.current !== null) {
        setIndiaElapsedSec(Math.floor((Date.now() - indiaStartTimeRef.current) / 1000));
      }
    }, 500);
    return () => clearInterval(interval);
  }, [isIndiaProcessing]);

  // ── Pre-fill UK form when an existing UK company is selected ──────────────
  // companyId is stored as ``uk_{company_number}`` (8-digit), so the CH number
  // can be derived from the suffix without an extra fetch.
  useEffect(() => {
    if (selectedCountry !== 'uk') return;
    if (companySelectionMode !== 'existing') return;
    if (!selectedExistingCompany) return;

    const cId: string = selectedExistingCompany.companyId || '';
    const name: string = selectedExistingCompany.companyName || '';
    const numberFromId = cId.startsWith('uk_') ? cId.slice(3) : '';

    if (name) setUkCompanyName(name);
    if (numberFromId) {
      setUkCompanyNumber(numberFromId.toUpperCase());
      setUkSearchSuppressed(true); // pre-filled — don't auto-search
    }
  }, [selectedCountry, companySelectionMode, selectedExistingCompany]);

  // ── Debounced UK Companies House autocomplete ─────────────────────────────
  // Hits GET /api/finance/uk/search-companies on every keystroke (>= 2 chars).
  // Backend caches each (query, limit) for 5 minutes so this stays cheap and
  // within the Companies House rate budget. Suppressed once the user picks a
  // result so re-rendering the input with the official name doesn't re-search.
  useEffect(() => {
    if (selectedCountry !== 'uk') return;
    if (ukSearchSuppressed) return;
    if (ukIsProcessing) return;

    const q = ukCompanyName.trim();
    if (q.length < 2) {
      setUkSearchResults([]);
      setUkSearchOpen(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setUkSearchLoading(true);
      try {
        const res = await ukCompanyAPI.searchCompanies(q, 10);
        if (cancelled) return;
        setUkSearchResults(res.results || []);
        setUkSearchOpen(true);
      } catch {
        if (!cancelled) {
          setUkSearchResults([]);
          setUkSearchOpen(false);
        }
      } finally {
        if (!cancelled) setUkSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, ukCompanyName, ukSearchSuppressed, ukIsProcessing]);

  // ── Debounced UK availability lookup ──────────────────────────────────────
  // Resolves the company on Companies House and returns the list of unique
  // fiscal years with available accounts filings. Used to gate time-range
  // buttons so a user cannot pick "10Y" if only 5Y of data exists.
  useEffect(() => {
    if (selectedCountry !== 'uk') return;

    const name = ukCompanyName.trim();
    const number = ukCompanyNumber.trim();

    const hasValidNumber = number.length > 0 && /^[0-9A-Z]{6,8}$/i.test(number);
    const hasUsableName = name.length >= 3;
    if (!hasValidNumber && !hasUsableName) {
      setUkAvailability(null);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setUkAvailabilityLoading(true);
      try {
        const res = await ukCompanyAPI.checkAvailability({
          company_name: name || number,
          company_code: hasValidNumber ? number : undefined,
        });
        if (controller.signal.aborted) return;
        setUkAvailability(res);

        // If the user's selected time range exceeds what's available, snap it down.
        if (res.status === 'success' && res.filing_count > 0) {
          const requested = parseInt(ukTimeRange.replace('year', '').replace('s', ''), 10) || 0;
          if (requested > res.filing_count) {
            const valid: UKTimeRange[] = (['1year', '2years', '3years', '5years', '10years'] as UKTimeRange[])
              .filter((r) => parseInt(r.replace('year', '').replace('s', ''), 10) <= res.filing_count);
            if (valid.length > 0) setUkTimeRange(valid[valid.length - 1]);
          }
        }
      } catch {
        if (!controller.signal.aborted) setUkAvailability(null);
      } finally {
        if (!controller.signal.aborted) setUkAvailabilityLoading(false);
      }
    }, 600);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, ukCompanyName, ukCompanyNumber]);

  // ── Elapsed-time ticker for the UK extraction loader ──────────────────────
  // Starts the moment ANY UK queue item enters `processing`; resets when none
  // are processing anymore. Drives the rich loader's progress bar + phase
  // stepper, exactly as in the old single-shot UI.
  useEffect(() => {
    if (!ukIsProcessing) {
      setUkElapsedSec(0);
      ukStartTimeRef.current = null;
      return;
    }
    if (ukStartTimeRef.current === null) ukStartTimeRef.current = Date.now();
    const interval = setInterval(() => {
      if (ukStartTimeRef.current !== null) {
        setUkElapsedSec(Math.floor((Date.now() - ukStartTimeRef.current) / 1000));
      }
    }, 500);
    return () => clearInterval(interval);
  }, [ukIsProcessing]);

  // ─────────────────────────────────────────────────────────────────────────
  // INDIA FLOW — autocomplete form + queue (mirrors UK)
  // ─────────────────────────────────────────────────────────────────────────

  const handleAddIndiaToQueue = () => {
    const name = indiaInputName.trim();
    if (!name) { toast.error('Please enter a company name'); return; }
    if (indiaQueue.some(item => item.companyName.toLowerCase() === name.toLowerCase())) {
      toast.warning(`"${name}" is already in the list`);
      return;
    }
    setIndiaQueue(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      companyName: name,
      dataType: indiaInputDataType,
      status: 'pending',
    }]);
    // Reset the form so the user can immediately queue another company.
    setIndiaInputName('');
    setIndiaInputTicker('');
    setMcSearchResults([]);
    setMcSearchError(null);
    setIndiaSearchOpen(false);
    setIndiaSearchSuppressed(false);
  };

  const handleRemoveFromQueue = (id: string) => {
    setIndiaQueue(prev => prev.filter(item => item.id !== id));
  };

  const extractCompanyId = (message?: string): string | null => {
    if (!message) return null;
    const m1 = message.match(/company_id=['"]([^'"]+)['"]/i);
    if (m1?.[1]) return m1[1];
    const m2 = message.match(/company_id=([a-z0-9_-]+)/i);
    return m2?.[1] ?? null;
  };

  // Triggers the India ingestion — the backend responds synchronously with the result.
  // shouldNavigate=true → on success, navigate to the company detail page.
  const runIngestionForItem = (item: IndiaQueueItem, shouldNavigate: boolean) => {
    (async () => {
      try {
        const trigger = await indiaCompanyAPI.triggerResearch({
          company_name: item.companyName,
          data_type: item.dataType,
        });

        const triggerStatus = (trigger?.status || '').toLowerCase();
        const isTriggerFailed = ['failed', 'error', 'not_found', 'failure', '0'].includes(triggerStatus)
          || (trigger as any)?.error;
        if (isTriggerFailed) {
          const technical = (trigger as any)?.error || trigger.message || trigger?.status || '';
          const friendly = `Could not load financial data for ${item.companyName}. Please verify the company name or try the other data type.`;
          setIndiaQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'failed', message: technical || friendly } : i));
          toast.error(friendly, { duration: 8000 });
          return;
        }

        // Synchronous response — result is in the trigger itself
        const resultData = (trigger as any)?.data ?? trigger;
        const friendlySuccess = `Financial data loaded for ${item.companyName}.`;
        setIndiaQueue(prev => prev.map(i => i.id === item.id
          ? { ...i, status: 'completed', message: friendlySuccess }
          : i));
        toast.success(friendlySuccess, { duration: 6000 });
        onResearchComplete?.();

        if (shouldNavigate) {
          // The mcapi response nests the id at data.metadata.company_id and also
          // embeds `company_id='…'` in the OUTER message — read both, not
          // resultData.company_id/.message (which don't exist), so a new company
          // reaches its detail page instead of the /companies list.
          const companyId =
            (resultData as any)?.metadata?.company_id ||
            (resultData as any)?.company_id ||
            (resultData as any)?.companyId ||
            extractCompanyId((trigger as any)?.message) ||
            extractCompanyId((resultData as any)?.message);
          if (companyId) {
            navigateAfterResearch(`/companies/${companyId}`, 1200);
          } else {
            toast.info('Redirecting to your companies…', { duration: 4000 });
            navigateAfterResearch('/companies', 1200);
          }
        }
      } catch (err: any) {
        const httpStatus = err?.response?.status;
        const errorMsg = httpStatus === 404
          ? `We couldn't find financial data for ${item.companyName}. Try the other data type or check the spelling.`
          : httpStatus === 422
          ? `Please check the company name and data type, then try again.`
          : `Could not load financial data for ${item.companyName}. Please try again.`;
        setIndiaQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'failed', message: errorMsg } : i));
        toast.error(errorMsg, { duration: 8000 });
      }
    })();
  };

  const handleRetryItem = (item: IndiaQueueItem) => {
    setIndiaQueue(prev => prev.map(i => i.id === item.id
      ? { ...i, status: 'processing', message: undefined }
      : i));
    runIngestionForItem(item, true);
  };

  const handleStartIndiaResearch = () => {
    const pending = indiaQueue.filter(item => item.status === 'pending');
    if (pending.length === 0) { toast.error('No companies queued for research'); return; }

    // Single-company queue → navigate to company page on completion
    const shouldNavigate = pending.length === 1;

    setIndiaQueue(prev =>
      prev.map(item => item.status === 'pending' ? { ...item, status: 'processing' } : item)
    );

    for (const item of pending) {
      runIngestionForItem(item, shouldNavigate);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // UK FLOW — autocomplete form + queue
  // ─────────────────────────────────────────────────────────────────────────

  const handleAddUKToQueue = () => {
    const name = ukCompanyName.trim();
    if (!name) { toast.error('Please enter a company name'); return; }

    const number = ukCompanyNumber.trim() || undefined;
    if (number && !/^[0-9A-Z]{6,8}$/i.test(number)) {
      toast.error('Companies House number must be 6–8 alphanumeric characters');
      return;
    }

    // De-dupe on CH number when present, name otherwise.
    const upper = number?.toUpperCase();
    const dupe = ukQueue.find(it =>
      (upper && it.companyNumber && it.companyNumber === upper)
      || (!upper && it.companyName.toLowerCase() === name.toLowerCase())
    );
    if (dupe) {
      toast.warning(`"${name}" is already in the list`);
      return;
    }

    setUkQueue(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      companyName: name,
      companyNumber: upper,
      timeRange: ukTimeRange,
      status: 'pending',
    }]);

    // Reset the form so the user can immediately queue another company.
    setUkCompanyName('');
    setUkCompanyNumber('');
    setUkSearchResults([]);
    setUkSearchOpen(false);
    setUkSearchSuppressed(false);
    setUkAvailability(null);
  };

  const handleRemoveUKFromQueue = (id: string) => {
    setUkQueue(prev => prev.filter(item => item.id !== id));
  };

  // Run one queued UK item — extracted from the old single-shot
  // `handleStartUKResearch` so the queue can drive many items independently.
  // shouldNavigate=true → on success, navigate to the company detail page.
  const runUKIngestionForItem = (item: UKQueueItem, shouldNavigate: boolean) => {
    (async () => {
      try {
        const res = await ukCompanyAPI.triggerResearch({
          company_name: item.companyName,
          company_code: item.companyNumber,
          data_type: 'annual',
          time_range: item.timeRange,
        });

        const status = (res?.status || '').toLowerCase() as 'success' | 'partial' | 'error';
        const companyId =
          res?.data?.metadata?.company_id ||
          (res as any)?.data?.company_id ||
          extractCompanyId((res as any)?.message);
        const resolvedName = res?.data?.sanitized_company_name || res?.data?.metadata?.company_name || item.companyName;
        const yearsLoaded = res?.data?.filings_processed ?? 0;

        if (status === 'error') {
          const msg = `Could not load financial data for ${item.companyName}. Please try again or check the company name.`;
          setUkQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'failed', message: res?.message || msg } : i));
          toast.error(msg, { duration: 8000 });
          return;
        }

        // For partial-with-no-years the backend supplies a specific `message`
        // explaining *why* (micro-entity exemption, scanned PDFs, etc.).
        // Prefer it over a generic line so the queue row tells the truth.
        const backendMessage = (res as any)?.message as string | undefined;
        const friendly = status === 'partial'
          ? (yearsLoaded > 0
              ? `Loaded ${yearsLoaded} year${yearsLoaded === 1 ? '' : 's'} for ${resolvedName} — some periods couldn't be processed.`
              : (backendMessage || `No structured financial data available for ${resolvedName} on Companies House.`))
          : (yearsLoaded > 0
              ? `${yearsLoaded} year${yearsLoaded === 1 ? '' : 's'} of financial data loaded for ${resolvedName}.`
              : `Financial data loaded for ${resolvedName}.`);

        // Status: only mark `completed` when we actually loaded years. Partial
        // with zero usable years is closer to "failed" from the user's POV —
        // the company exists in the graph, but there's nothing to view.
        const rowStatus: 'completed' | 'failed' =
          status === 'partial' && yearsLoaded === 0 ? 'failed' : 'completed';

        setUkQueue(prev => prev.map(i => i.id === item.id
          ? { ...i, status: rowStatus, message: friendly, companyId }
          : i));
        if (status === 'partial') toast.warning(friendly, { duration: 8000 });
        else toast.success(friendly, { duration: 6000 });
        onResearchComplete?.();

        // Don't navigate to a blank company page when no usable data was loaded.
        if (shouldNavigate && yearsLoaded > 0) {
          if (companyId) {
            navigateAfterResearch(`/companies/${companyId}`, 1500);
          } else {
            navigateAfterResearch('/companies', 1500);
          }
        }
      } catch (err: any) {
        const httpStatus = err?.response?.status;
        const errorMsg = httpStatus === 502
          ? `Companies House is unreachable right now. Please try again in a moment.`
          : httpStatus === 404
            ? `We couldn't find any filings for "${item.companyName}". Try the official registered name or provide the Companies House number.`
            : `Could not load financial data for ${item.companyName}. Please try again.`;
        setUkQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'failed', message: errorMsg } : i));
        toast.error(errorMsg, { duration: 8000 });
      }
    })();
  };

  const handleRetryUKItem = (item: UKQueueItem) => {
    setUkQueue(prev => prev.map(i => i.id === item.id
      ? { ...i, status: 'processing', message: undefined }
      : i));
    runUKIngestionForItem(item, true);
  };

  const handleStartUKResearch = () => {
    const pending = ukQueue.filter(item => item.status === 'pending');
    if (pending.length === 0) { toast.error('No companies queued for research'); return; }

    // Single-company queue → navigate to that company on completion.
    const shouldNavigate = pending.length === 1;

    setUkQueue(prev =>
      prev.map(item => item.status === 'pending' ? { ...item, status: 'processing' } : item)
    );

    for (const item of pending) {
      runUKIngestionForItem(item, shouldNavigate);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // US FLOW — autocomplete form + queue (mirrors UK/India)
  // ─────────────────────────────────────────────────────────────────────────

  // Debounced SEC autocomplete search.
  const performSearch = useCallback(async (query: string) => {
    setIsSearching(true);
    try {
      const results = await standaloneCompaniesAPI.searchSECCompanies(query);
      setSearchResults(results.map((c: any) => ({
        companyName: c.name,
        ticker: c.ticker,
        cik: c.cik,
        exchange: c.exchange,
      })));
      setUsSearchOpen(true);
    } catch {
      toast.error('Failed to search companies');
      setSearchResults([]);
      setUsSearchOpen(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCountry !== 'us') return;
    if (researchMode !== 'sec') return;
    if (usSearchSuppressed) return;
    if (isUSProcessing) return;

    const q = usCompanyName.trim();
    if (q.length < 4) {
      setSearchResults([]);
      setUsSearchOpen(false);
      return;
    }
    const t = setTimeout(() => performSearch(q), 500);
    return () => clearTimeout(t);
  }, [usCompanyName, selectedCountry, researchMode, usSearchSuppressed, isUSProcessing, performSearch]);

  // Elapsed-time ticker for the US extraction loader.
  useEffect(() => {
    if (!isUSProcessing) {
      setUsElapsedSec(0);
      usStartTimeRef.current = null;
      return;
    }
    if (usStartTimeRef.current === null) usStartTimeRef.current = Date.now();
    const interval = setInterval(() => {
      if (usStartTimeRef.current !== null) {
        setUsElapsedSec(Math.floor((Date.now() - usStartTimeRef.current) / 1000));
      }
    }, 500);
    return () => clearInterval(interval);
  }, [isUSProcessing]);

  const handleAddUSToQueue = () => {
    const name = usCompanyName.trim();
    if (!name) { toast.error('Please enter a company name'); return; }

    const ticker = usCompanyTicker.trim().toUpperCase() || undefined;
    const cik = usCompanyCik.trim() || undefined;

    // De-dupe on ticker (or CIK or name).
    const dupe = usQueue.find(it =>
      (ticker && it.ticker === ticker)
      || (cik && it.cik === cik)
      || (!ticker && !cik && it.companyName.toLowerCase() === name.toLowerCase())
    );
    if (dupe) {
      toast.warning(`"${name}" is already in the list`);
      return;
    }

    setUsQueue(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      companyName: name,
      ticker,
      cik,
      timeRange,
      status: 'pending',
    }]);

    // Reset the form so the user can immediately queue another company.
    setUsCompanyName('');
    setUsCompanyTicker('');
    setUsCompanyCik('');
    setSearchResults([]);
    setUsSearchOpen(false);
    setUsSearchSuppressed(false);
  };

  const handleRemoveUSFromQueue = (id: string) => {
    setUsQueue(prev => prev.filter(item => item.id !== id));
  };

  // Run one queued US item — two-phase:
  //   Phase 1: POST /finance/us-financial-data — fast XBRL fetch + calc + save.
  //            Returns a job_id for the deeper SEC research that runs in the
  //            backend's `_run_background_pipeline`.
  //   Phase 2: poll GET /companies/research/jobs/{jobId} until completed or
  //            failed. Only THEN do we mark the queue row as completed and
  //            (if single-company) navigate to the company page. Without
  //            phase 2 the queue would falsely report "done" while the SEC
  //            research extraction is still running, leading to a confusing
  //            "data still being added" experience on the company page.
  const runUSIngestionForItem = (item: USQueueItem, shouldNavigate: boolean) => {
    (async () => {
      let companyId: string | undefined;
      let jobId: string | undefined;
      let resolvedName = item.companyName;
      let yearsLoaded = 0;

      // ── Phase 1: financial data ─────────────────────────────────────────
      try {
        const res = await standaloneCompaniesAPI.fetchUSFinancialData({
          company_name: item.companyName,
          company_code: item.ticker,
          time_range: item.timeRange,
        });

        const status = String((res as any)?.status || 'success').toLowerCase();
        const data = (res as any)?.data ?? res;
        const meta = data?.metadata || {};
        companyId = meta.company_id || data?.company_id || extractCompanyId((res as any)?.message);
        jobId = meta.job_id;
        resolvedName = data?.sanitized_company_name || meta.company_name || item.companyName;
        yearsLoaded = data?.data?.length ?? data?.filings_processed ?? 0;

        if (status === 'error') {
          const msg = `Could not load financial data for ${item.companyName}. Please try again or check the ticker.`;
          setUsQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'failed', message: (res as any)?.message || msg } : i));
          toast.error(msg, { duration: 8000 });
          return;
        }

        if (status === 'partial' && yearsLoaded === 0) {
          // No usable data + no SEC job to poll — short-circuit.
          const msg = (res as any)?.message || `No structured SEC data available for ${resolvedName}.`;
          setUsQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'failed', message: msg, companyId } : i));
          toast.warning(msg, { duration: 8000 });
          return;
        }
      } catch (err: any) {
        const httpStatus = err?.response?.status;
        const errorMsg = httpStatus === 404
          ? `We couldn't find SEC filings for "${item.companyName}". Try the official ticker (e.g. AAPL).`
          : httpStatus === 502
            ? `SEC EDGAR is unreachable right now. Please try again in a moment.`
            : `Could not load financial data for ${item.companyName}. Please try again.`;
        setUsQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: 'failed', message: errorMsg } : i));
        toast.error(errorMsg, { duration: 8000 });
        return;
      }

      // ── Phase 2: poll SEC research job ──────────────────────────────────
      // No job to poll → mark complete now (rare; older backends).
      if (!jobId) {
        const msg = yearsLoaded > 0
          ? `${yearsLoaded} year${yearsLoaded === 1 ? '' : 's'} of financial data loaded for ${resolvedName}.`
          : `Financial data loaded for ${resolvedName}.`;
        setUsQueue(prev => prev.map(i => i.id === item.id
          ? { ...i, status: 'completed', message: msg, companyId }
          : i));
        toast.success(msg, { duration: 6000 });
        onResearchComplete?.();
        if (shouldNavigate && companyId) {
          navigateAfterResearch(`/companies/${companyId}`, 1500);
        }
        return;
      }

      // Show the polling state on the row.
      setUsQueue(prev => prev.map(i => i.id === item.id
        ? { ...i, message: `Financial data loaded · extracting SEC reports…`, companyId }
        : i));

      const POLL_INTERVAL_MS = 3000;
      const MAX_POLLS = 240; // ~12 min ceiling — SEC extraction can be slow
      let attempts = 0;
      while (attempts < MAX_POLLS) {
        attempts++;
        try {
          const s = await standaloneCompaniesAPI.getJobStatus(jobId);
          const jobStatus = String((s as any)?.status || '').toLowerCase();

          if (jobStatus === 'completed') {
            const completed = (s as any)?.progress?.completed ?? 0;
            const total = (s as any)?.progress?.total ?? 0;
            const friendly = total > 0
              ? `${resolvedName} — ${completed}/${total} SEC report${total === 1 ? '' : 's'} processed.`
              : `Research completed for ${resolvedName}.`;
            setUsQueue(prev => prev.map(i => i.id === item.id
              ? { ...i, status: 'completed', message: friendly, companyId }
              : i));
            toast.success(friendly, { duration: 6000 });
            onResearchComplete?.();
            if (shouldNavigate && companyId) {
              navigateAfterResearch(`/companies/${companyId}`, 1500);
            }
            return;
          }

          if (jobStatus === 'failed') {
            const err = (s as any)?.error || 'SEC research failed';
            setUsQueue(prev => prev.map(i => i.id === item.id
              ? { ...i, status: 'failed', message: `${resolvedName}: ${err}`, companyId }
              : i));
            toast.error(`${resolvedName}: ${err}`, { duration: 8000 });
            return;
          }

          // Show running progress on the row.
          const completed = (s as any)?.progress?.completed ?? 0;
          const total = (s as any)?.progress?.total ?? 0;
          const phase = (s as any)?.currentPhase;
          const phaseLabel = phase === 'fetching_reports' ? 'Fetching SEC reports'
            : phase === 'processing_reports' ? 'Processing SEC filings'
            : phase === 'generating_summary' ? 'Generating summary'
            : 'Extracting SEC research';
          setUsQueue(prev => prev.map(i => i.id === item.id
            ? { ...i, message: total > 0 ? `${phaseLabel} · ${completed}/${total}` : `${phaseLabel}…`, companyId }
            : i));
        } catch {
          // Transient — keep polling.
        }
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      }

      // Timed out — leave the row as completed-with-warning (financial data is
      // already saved; SEC research will continue in the background).
      const timeoutMsg = `${resolvedName}: financial data loaded; SEC research is taking longer than expected and will continue in the background.`;
      setUsQueue(prev => prev.map(i => i.id === item.id
        ? { ...i, status: 'completed', message: timeoutMsg, companyId }
        : i));
      toast.warning(timeoutMsg, { duration: 8000 });
      onResearchComplete?.();
      if (shouldNavigate && companyId) {
        navigateAfterResearch(`/companies/${companyId}`, 1500);
      }
    })();
  };

  const handleRetryUSItem = (item: USQueueItem) => {
    setUsQueue(prev => prev.map(i => i.id === item.id
      ? { ...i, status: 'processing', message: undefined }
      : i));
    runUSIngestionForItem(item, true);
  };

  const handleStartUSResearch = () => {
    const pending = usQueue.filter(item => item.status === 'pending');
    if (pending.length === 0) { toast.error('No companies queued for research'); return; }

    const shouldNavigate = pending.length === 1;

    setUsQueue(prev =>
      prev.map(item => item.status === 'pending' ? { ...item, status: 'processing' } : item)
    );

    for (const item of pending) {
      runUSIngestionForItem(item, shouldNavigate);
    }
  };

  // Completion handler shared by both embedded PDF panels: close the wizard,
  // notify the parent, and open the freshly-created company.
  const handlePdfPanelComplete = (companyId: string) => {
    onClose();
    onResearchComplete?.();
    navigateAfterResearch(`/companies/${companyId}`);
  };

  // Close request from backdrop / header buttons — refused (with a hint)
  // while a PDF-statements job is running.
  const handleRequestClose = () => {
    if (isPdfPanelRunningRef.current) {
      toast.info('PDF extraction is in progress — please wait for it to finish.');
      return;
    }
    onClose();
  };

  if (!isPage && !isOpen) return null;

  // ── Breadcrumb ─────────────────────────────────────────────────────────────
  const breadcrumb =
    selectedCountry === null
      ? 'Select Country'
      : selectedCountry === 'india'
        ? indiaResearchMode === null
          ? 'India → Select Source'
          : indiaResearchMode === 'search'
            ? 'India → BSE/NSE Company Search'
            : 'India → Import Annual Report PDF'
        : selectedCountry === 'uk'
          ? 'UK → Companies House Lookup'
        : researchMode === null
          ? 'US → Select Source'
          : researchMode === 'sec'
            ? 'US → SEC EDGAR Lookup'
            : 'US → Import Annual Report PDF';

  // ── Queue status counts ────────────────────────────────────────────────────
  const queueCounts = {
    pending: indiaQueue.filter(i => i.status === 'pending').length,
    processing: indiaQueue.filter(i => i.status === 'processing').length,
    completed: indiaQueue.filter(i => i.status === 'completed').length,
    failed: indiaQueue.filter(i => i.status === 'failed').length,
  };

  const ukQueueCounts = {
    pending: ukQueue.filter(i => i.status === 'pending').length,
    processing: ukQueue.filter(i => i.status === 'processing').length,
    completed: ukQueue.filter(i => i.status === 'completed').length,
    failed: ukQueue.filter(i => i.status === 'failed').length,
  };

  const usQueueCounts = {
    pending: usQueue.filter(i => i.status === 'pending').length,
    processing: usQueue.filter(i => i.status === 'processing').length,
    completed: usQueue.filter(i => i.status === 'completed').length,
    failed: usQueue.filter(i => i.status === 'failed').length,
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className={isPage
        ? "min-h-screen bg-neutral-50"
        : "fixed inset-0 z-50 overflow-y-auto"
      }>
        {/* Backdrop — modal only */}
        {!isPage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={handleRequestClose}
            aria-hidden="true"
          />
        )}

        {/* Panel */}
        <div className={isPage
          ? "max-w-4xl mx-auto px-6 py-6"
          : "flex min-h-full items-start justify-center p-4 pt-8 pb-8"
        }>
          <div
            className={isPage
              ? "bg-white rounded-xl border border-neutral-200 shadow-sm w-full overflow-hidden"
              : "relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            }
            onClick={isPage ? undefined : e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={isPage
              ? "flex items-center justify-between px-6 py-4 border-b border-neutral-200"
              : "sticky top-0 z-10 bg-white flex items-center justify-between px-6 py-4 border-b border-neutral-200"
            }>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-3">
                  <Building2 className="h-7 w-7 text-primary-600" />
                  Research New Company
                </h1>
                <p className="text-neutral-500 text-sm mt-0.5">{breadcrumb}</p>
              </div>
              {isPage ? (
                <button
                  onClick={handleRequestClose}
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 transition-colors disabled:opacity-40"
                  disabled={isPdfPanelRunning}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Companies
                </button>
              ) : (
                <button
                  onClick={handleRequestClose}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors ml-4 p-1 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-40"
                  aria-label="Close"
                  disabled={isPdfPanelRunning}
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="p-6 space-y-6">

              {/* ── STEP 0: COUNTRY SELECTION ──────────────────────────────── */}
              {selectedCountry === null && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-neutral-900 mb-2">Select Country</h2>
                    <p className="text-neutral-600">Choose the market where the company is listed</p>
                  </div>

                  <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {/* India */}
                    {(() => {
                      const live = isCountryLive('india');
                      return (
                        <Card
                          className={live
                            ? "cursor-pointer transition-all hover:shadow-lg hover:ring-2 hover:ring-orange-400"
                            : "opacity-60 cursor-not-allowed border-dashed"}
                          onClick={live ? () => setSelectedCountry('india') : undefined}
                        >
                          <CardBody className="text-center p-6">
                            <div className="text-4xl mb-3">🇮🇳</div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-1">India</h3>
                            <p className="text-xs text-neutral-500 mb-3">BSE / NSE listed companies</p>
                            <div className="space-y-1.5 text-xs text-neutral-600 text-left">
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
                                <span>Moneycontrol & NSE data</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
                                <span>Consolidated & standalone</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                <span>Private PDF upload</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                                <span>INR (Crores)</span>
                              </div>
                            </div>
                            {!live && (
                              <div className="mt-3 inline-block px-2.5 py-0.5 bg-neutral-100 text-neutral-500 text-xs font-medium rounded-full">
                                Coming Soon
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      );
                    })()}

                    {/* US */}
                    {(() => {
                      const live = isCountryLive('us');
                      return (
                        <Card
                          className={live
                            ? "cursor-pointer transition-all hover:shadow-lg hover:ring-2 hover:ring-blue-400"
                            : "opacity-60 cursor-not-allowed border-dashed"}
                          onClick={live ? () => setSelectedCountry('us') : undefined}
                        >
                          <CardBody className="text-center p-6">
                            <div className="text-4xl mb-3">🇺🇸</div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-1">United States</h3>
                            <p className="text-xs text-neutral-500 mb-3">SEC EDGAR & PDF uploads</p>
                            <div className="space-y-1.5 text-xs text-neutral-600 text-left">
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                                <span>SEC EDGAR (10-K / 10-Q)</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                                <span>XBRL structured data</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                <span>Private PDF upload</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                                <span>USD (Millions)</span>
                              </div>
                            </div>
                            {!live && (
                              <div className="mt-3 inline-block px-2.5 py-0.5 bg-neutral-100 text-neutral-500 text-xs font-medium rounded-full">
                                Coming Soon
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      );
                    })()}

                    {/* UK */}
                    {(() => {
                      const live = isCountryLive('uk');
                      return (
                        <Card
                          className={live
                            ? "cursor-pointer transition-all hover:shadow-lg hover:ring-2 hover:ring-indigo-400"
                            : "opacity-60 cursor-not-allowed border-dashed"}
                          onClick={live ? () => setSelectedCountry('uk') : undefined}
                        >
                          <CardBody className="text-center p-6">
                            <div className="text-4xl mb-3">🇬🇧</div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-1">United Kingdom</h3>
                            <p className="text-xs text-neutral-500 mb-3">Companies House iXBRL accounts</p>
                            <div className={`space-y-1.5 text-xs text-left ${live ? 'text-neutral-600' : 'text-neutral-500'}`}>
                              <div className="flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${live ? 'bg-indigo-500' : 'bg-neutral-300'}`} />
                                <span>Companies House filings</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${live ? 'bg-indigo-500' : 'bg-neutral-300'}`} />
                                <span>iXBRL annual accounts</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${live ? 'bg-green-500' : 'bg-neutral-300'}`} />
                                <span>GBP (Millions)</span>
                              </div>
                            </div>
                            {!live && (
                              <div className="mt-3 inline-block px-2.5 py-0.5 bg-neutral-100 text-neutral-500 text-xs font-medium rounded-full">
                                Coming Soon
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      );
                    })()}

                    {/* EU */}
                    <Card className="opacity-60 cursor-not-allowed border-dashed">
                      <CardBody className="text-center p-6">
                        <div className="text-4xl mb-3">🇪🇺</div>
                        <h3 className="text-lg font-semibold text-neutral-900 mb-1">Europe</h3>
                        <p className="text-xs text-neutral-500 mb-3">EU listed companies</p>
                        <div className="space-y-1.5 text-xs text-neutral-500 text-left">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-neutral-300 shrink-0" />
                            <span>ESMA & Euronext filings</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-neutral-300 shrink-0" />
                            <span>EUR (Millions)</span>
                          </div>
                        </div>
                        <div className="mt-3 inline-block px-2.5 py-0.5 bg-neutral-100 text-neutral-500 text-xs font-medium rounded-full">
                          Coming Soon
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </div>
              )}

              {/* ── COMPANY SELECTION MODE (shown after country selected) ──── */}
              {selectedCountry !== null && (
                <Card>
                  <CardBody className="p-5">
                    <h3 className="text-base font-semibold text-neutral-900 mb-3">Company Selection</h3>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setCompanySelectionMode('new');
                          setSelectedExistingCompany(null);
                          // Reset pre-filled state so source selection is shown again
                          if (selectedCountry === 'us') {
                            setUsCompanyName('');
                            setUsCompanyTicker('');
                            setUsCompanyCik('');
                            setSearchResults([]);
                            setUsSearchOpen(false);
                            setUsSearchSuppressed(false);
                            setResearchMode(null);
                          }
                          if (selectedCountry === 'india') {
                            setIndiaResearchMode(null);
                            setIndiaInputName('');
                            setIndiaInputTicker('');
                            setMcSearchResults([]);
                            setIndiaSearchOpen(false);
                            setIndiaSearchSuppressed(false);
                          }
                        }}
                        className={`flex-1 p-3 rounded-lg border-2 transition-all text-sm ${
                          companySelectionMode === 'new'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <div className="font-semibold text-neutral-900">Create New Company</div>
                        <div className="text-neutral-500 text-xs mt-0.5">Start fresh</div>
                      </button>
                      <button
                        onClick={() => setCompanySelectionMode('existing')}
                        className={`flex-1 p-3 rounded-lg border-2 transition-all text-sm ${
                          companySelectionMode === 'existing'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <div className="font-semibold text-neutral-900">Add to Existing</div>
                        <div className="text-neutral-500 text-xs mt-0.5">Append data to existing company</div>
                      </button>
                    </div>

                    {companySelectionMode === 'existing' && (
                      <div className="mt-4">
                        {isLoadingCompanies ? (
                          <div className="flex items-center gap-2 text-neutral-600 text-sm py-4">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading companies...
                          </div>
                        ) : (
                          (() => {
                            const filteredCompanies = existingCompanies.filter(c => {
                              const id: string = c.companyId || '';
                              const isIndia = id.startsWith('ind-') || id.startsWith('ind_');
                              const isUK    = id.startsWith('uk_')  || id.startsWith('uk-');
                              if (selectedCountry === 'us')    return !isIndia && !isUK;
                              if (selectedCountry === 'india') return isIndia;
                              if (selectedCountry === 'uk')    return isUK;
                              return true;
                            });
                            return filteredCompanies.length === 0 ? (
                              <p className="text-sm text-neutral-500 py-4">No existing {selectedCountry === 'us' ? 'US' : selectedCountry === 'india' ? 'Indian' : selectedCountry === 'uk' ? 'UK' : ''} companies. Please create a new one.</p>
                            ) : (
                              <>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Select Company</label>
                                <select
                                  value={selectedExistingCompany?.companyId || ''}
                                  onChange={e => {
                                    const c = filteredCompanies.find(x => x.companyId === e.target.value) || null;
                                    setSelectedExistingCompany(c);

                                    if (!c) {
                                      // User cleared the selection — reset downstream
                                      if (selectedCountry === 'us') {
                                        setUsCompanyName('');
                                        setUsCompanyTicker('');
                                        setUsCompanyCik('');
                                        setSearchResults([]);
                                        setUsSearchOpen(false);
                                        setUsSearchSuppressed(false);
                                        setResearchMode(null);
                                      }
                                      if (selectedCountry === 'india') {
                                        setIndiaResearchMode(null);
                                        setIndiaInputName(''); setIndiaInputTicker('');
                                        setMcSearchResults([]); setIndiaSearchOpen(false);
                                        setIndiaSearchSuppressed(false);
                                      }
                                      return;
                                    }

                                    // US: pre-fill the form so the user can pick a time range and add
                                    // to the queue. Suppress auto-search to avoid re-querying after
                                    // the name is populated programmatically. A company without a CIK
                                    // was PDF-created; the finance-pdf pipeline resolves the target
                                    // from the document (no companyId parameter), so stay on source
                                    // selection where the disabled PDF card explains this.
                                    if (selectedCountry === 'us') {
                                      if (c.cik) {
                                        setResearchMode('sec');
                                        setUsCompanyName(c.companyName);
                                        setUsCompanyTicker(c.ticker || '');
                                        setUsCompanyCik(c.cik);
                                        setUsSearchSuppressed(true);
                                      } else {
                                        setResearchMode(null);
                                        toast.info('This company was created from a PDF. To add more reports, use Create New Company → Private Company (PDF) — the document is matched to the company automatically.');
                                      }
                                    }

                                    // India: pre-fill the form so the user can adjust report type and
                                    // add to the queue. Suppress auto-search to avoid re-querying after
                                    // the name is populated programmatically.
                                    if (selectedCountry === 'india') {
                                      setIndiaResearchMode('search');
                                      setIndiaInputName(c.companyName);
                                      setIndiaInputTicker(c.ticker || '');
                                      setIndiaSearchSuppressed(true);
                                      setMcSearchResults([]);
                                      setIndiaSearchOpen(false);
                                    }
                                  }}
                                  className="w-full p-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                                >
                                  <option value="">Select a company...</option>
                                  {filteredCompanies.map(c => (
                                    <option key={c.companyId} value={c.companyId}>
                                      {c.companyName} {c.ticker ? `(${c.ticker})` : ''} — {c.reportCount || 0} reports
                                    </option>
                                  ))}
                                </select>
                              </>
                            );
                          })()
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* INDIA FLOW                                                  */}
              {/* ═══════════════════════════════════════════════════════════ */}
              {selectedCountry === 'india' && (
                <>
                  <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <span className="text-xl">🇮🇳</span>
                    <div className="text-sm">
                      <span className="font-semibold text-orange-900">India (BSE/NSE)</span>
                      <span className="text-orange-700 ml-2">— Moneycontrol & NSE data</span>
                    </div>
                    <button
                      className="ml-auto text-xs text-orange-600 hover:text-orange-800 underline disabled:opacity-40"
                      onClick={() => { setSelectedCountry(null); setIndiaResearchMode(null); }}
                      disabled={isIndiaProcessing || isPdfPanelRunning}
                    >
                      Change
                    </button>
                  </div>

                  {/* India Step 1: Source selection */}
                  {indiaResearchMode === null && (
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-xl font-semibold text-neutral-900 mb-2">Select Research Source</h2>
                        <p className="text-neutral-600">Choose how you want to research Indian companies</p>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* BSE/NSE Search */}
                        <Card
                          className="cursor-pointer transition-all hover:shadow-lg"
                          onClick={() => setIndiaResearchMode('search')}
                        >
                          <CardBody className="text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-orange-100 mx-auto flex items-center justify-center mb-4">
                              <Search className="h-8 w-8 text-orange-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-2">BSE/NSE Listed Company</h3>
                            <p className="text-sm text-neutral-600 mb-4">
                              Auto-fetch financial data via Moneycontrol & NSE
                            </p>
                            <div className="space-y-2 text-sm text-neutral-700">
                              {[
                                ['Search by company name', 'green'],
                                ['Consolidated & standalone', 'green'],
                                ['Historical financial data', 'green'],
                                ['Add multiple companies at once', 'orange'],
                              ].map(([f, c]) => (
                                <div key={f} className="flex items-center justify-center gap-2">
                                  <div className={`w-1.5 h-1.5 rounded-full bg-${c}-500`} /><span>{f}</span>
                                </div>
                              ))}
                            </div>
                          </CardBody>
                        </Card>

                        {/* PDF Upload — the finance-pdf pipeline resolves the target
                            company from the document itself (no companyId parameter),
                            so a manually picked company can't be honored in
                            "Add to Existing" mode. */}
                        <Card
                          className={companySelectionMode === 'existing'
                            ? 'opacity-60 cursor-not-allowed'
                            : 'cursor-pointer transition-all hover:shadow-lg'}
                          onClick={companySelectionMode === 'existing' ? undefined : () => setIndiaResearchMode('pdf')}
                        >
                          <CardBody className="text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-secondary-100 mx-auto flex items-center justify-center mb-4">
                              <Upload className="h-8 w-8 text-secondary-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Private Company (PDF)</h3>
                            <p className="text-sm text-neutral-600 mb-4">
                              Upload annual reports or financial statements
                            </p>
                            <div className="space-y-2 text-sm text-neutral-700">
                              {[
                                ['Upload annual report PDF', 'amber'],
                                ['AI-powered extraction', 'amber'],
                                ['Company auto-detected from the PDF', 'green'],
                              ].map(([f, c]) => (
                                <div key={f} className="flex items-center justify-center gap-2">
                                  <div className={`w-1.5 h-1.5 rounded-full bg-${c}-500`} /><span>{f}</span>
                                </div>
                              ))}
                            </div>
                            {companySelectionMode === 'existing' && (
                              <p className="text-xs text-neutral-500 mt-4">
                                The company is identified from the PDF itself — switch to
                                &ldquo;Create New Company&rdquo; to upload. Reports for an
                                existing company are matched to it automatically.
                              </p>
                            )}
                          </CardBody>
                        </Card>
                      </div>
                    </div>
                  )}

                  {/* India Step 2a: BSE/NSE search + queue */}
                  {indiaResearchMode === 'search' && (
                    <div className="space-y-5">
                      <h2 className="text-xl font-semibold text-neutral-900">Search BSE / NSE Company</h2>

                      {/* Single-company form: Name (with autocomplete) + NSE/BSE Ticker + Report Type + Add to Queue */}
                      <Card>
                        <CardBody className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                              Company Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
                              <input
                                type="text"
                                value={indiaInputName}
                                onChange={e => {
                                  setIndiaInputName(e.target.value);
                                  // Typing again invalidates a prior pick.
                                  if (indiaSearchSuppressed) {
                                    setIndiaSearchSuppressed(false);
                                    setIndiaInputTicker('');
                                  }
                                }}
                                onFocus={() => {
                                  if (!indiaSearchSuppressed && indiaInputName.trim().length >= 3 && mcSearchResults.length > 0) {
                                    setIndiaSearchOpen(true);
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(() => setIndiaSearchOpen(false), 150);
                                }}
                                onKeyDown={e => { if (e.key === 'Enter' && !isIndiaProcessing) handleAddIndiaToQueue(); }}
                                disabled={isIndiaProcessing}
                                placeholder="e.g. Hindustan Unilever, Infosys, HDFC Bank"
                                className="w-full pl-9 pr-9 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-neutral-50 disabled:cursor-not-allowed"
                                autoComplete="off"
                              />
                              {mcSearchLoading && (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 animate-spin" />
                              )}

                              {/* Autocomplete dropdown — Yahoo Finance / Moneycontrol live results */}
                              {indiaSearchOpen && !isIndiaProcessing && indiaInputName.trim().length >= 3 && (
                                <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg">
                                  {mcSearchLoading && mcSearchResults.length === 0 && (
                                    <div className="flex items-center justify-center py-4 text-xs text-neutral-500">
                                      <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                      Searching Moneycontrol…
                                    </div>
                                  )}
                                  {!mcSearchLoading && mcSearchResults.length === 0 && (
                                    <p className="py-3 text-center text-xs text-neutral-500">
                                      No companies found
                                    </p>
                                  )}
                                  {mcSearchResults.map(item => (
                                    <button
                                      key={item.sc_id ?? `${item.stock_name}-${item.sc_nse_symbol ?? item.sc_bse_symbol ?? ''}`}
                                      type="button"
                                      onMouseDown={e => e.preventDefault()}
                                      onClick={() => {
                                        setIndiaInputName(item.stock_name);
                                        setIndiaInputTicker(item.sc_nse_symbol || item.sc_bse_symbol || '');
                                        setIndiaSearchSuppressed(true);
                                        setIndiaSearchOpen(false);
                                        setMcSearchResults([]);
                                      }}
                                      className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-orange-50 border-b border-neutral-100 last:border-b-0"
                                    >
                                      <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-neutral-900 truncate">
                                          {item.stock_name}
                                        </p>
                                        <div className="flex items-center gap-2 text-[11px] text-neutral-500 mt-0.5">
                                          {item.sc_nse_symbol && (
                                            <span className="font-medium text-orange-600">NSE: {item.sc_nse_symbol}</span>
                                          )}
                                          {item.sc_bse_symbol && (
                                            <span className="font-medium text-blue-600">BSE: {item.sc_bse_symbol}</span>
                                          )}
                                          {!item.sc_nse_symbol && !item.sc_bse_symbol && item.sc_id && (
                                            <span className="font-mono">ID: {item.sc_id}</span>
                                          )}
                                        </div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-neutral-500 mt-1">
                              Live results from Yahoo Finance — pick one to auto-fill the NSE/BSE ticker.
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                              NSE / BSE Ticker <span className="text-neutral-400 font-normal">(optional)</span>
                            </label>
                            <div className="relative">
                              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
                              <input
                                type="text"
                                value={indiaInputTicker}
                                onChange={e => setIndiaInputTicker(e.target.value.toUpperCase())}
                                disabled={isIndiaProcessing}
                                placeholder="e.g. HINDUNILVR, INFY, HDFCBANK"
                                className="w-full pl-9 pr-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-neutral-50 disabled:cursor-not-allowed font-mono"
                              />
                            </div>
                            <p className="text-xs text-neutral-500 mt-1">
                              Leave blank to auto-resolve by name. Providing the ticker speeds up lookup.
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Report Type</label>
                            <div className="flex gap-2">
                              {(['consolidated', 'standalone'] as DataType[]).map(dt => (
                                <button
                                  key={dt}
                                  onClick={() => setIndiaInputDataType(dt)}
                                  disabled={isIndiaProcessing}
                                  className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-all capitalize ${
                                    indiaInputDataType === dt
                                      ? 'border-orange-500 bg-orange-100 text-orange-700'
                                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                                  } disabled:opacity-50`}
                                >
                                  {dt}
                                </button>
                              ))}
                            </div>
                          </div>

                          <Button
                            variant="primary"
                            className="w-full"
                            onClick={handleAddIndiaToQueue}
                            disabled={isIndiaProcessing || indiaInputName.trim().length === 0}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add to Research Queue
                          </Button>
                        </CardBody>
                      </Card>

                      {/* Rich extraction loader — only when ONE company is processing.
                          When multiple run concurrently the elaborate phase stepper is
                          ambiguous (which company is "Saving to graph" referring to?),
                          so we fall back to the per-row status in the queue list below. */}
                      {queueCounts.processing === 1 && (() => {
                        const activeItem = indiaQueue.find(i => i.status === 'processing');
                        if (!activeItem) return null;

                        const phases = [
                          { id: 'resolve',   label: 'Resolving company',     hint: 'Looking up NSE/BSE symbol & sector via Azure OpenAI', icon: Building2,  startSec: 0   },
                          { id: 'scrape',    label: 'Scraping Moneycontrol', hint: 'Fetching P&L + Balance Sheet (consolidated/standalone)', icon: Download,   startSec: 8   },
                          { id: 'market',    label: 'Fetching market data',  hint: 'Beta, share price history, market cap',               icon: FileSearch, startSec: 30  },
                          { id: 'calc',      label: 'Calculating metrics',   hint: 'WACC, ROIC, EV, CAP, NOPAT, CAGR',                    icon: Sparkles,   startSec: 55  },
                          { id: 'save',      label: 'Saving to graph',       hint: 'Persisting company + year nodes to Neo4j',            icon: Database,   startSec: 80  },
                        ];
                        const activeIdx = phases.reduce(
                          (idx, p, i) => (indiaElapsedSec >= p.startSec ? i : idx),
                          0
                        );
                        const mins = Math.floor(indiaElapsedSec / 60);
                        const secs = indiaElapsedSec % 60;
                        const elapsedLabel = mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
                        const progressPct = Math.min(95, Math.round((indiaElapsedSec / 100) * 95));

                        return (
                          <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 via-white to-orange-50/40 overflow-hidden shadow-sm">
                            <div className="px-5 py-4 border-b border-orange-100 bg-white/60 backdrop-blur">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                  <div className="relative h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                    <Loader2 className="h-5 w-5 text-orange-600 animate-spin" />
                                    <div className="absolute inset-0 rounded-full ring-4 ring-orange-200/50 animate-ping" />
                                  </div>
                                  <div>
                                    <h3 className="text-sm font-semibold text-orange-900">Fetching from Moneycontrol</h3>
                                    <p className="text-xs text-orange-700 mt-0.5">
                                      {activeItem.companyName}
                                      <span className="text-neutral-500 ml-1.5 capitalize">· {activeItem.dataType}</span>
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="text-[11px] uppercase tracking-wide text-neutral-500">Elapsed</div>
                                  <div className="text-sm font-mono font-semibold text-orange-900 tabular-nums">{elapsedLabel}</div>
                                </div>
                              </div>
                              <div className="mt-3 w-full bg-orange-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="bg-gradient-to-r from-orange-500 to-orange-600 h-full rounded-full transition-all duration-1000 ease-out"
                                  style={{ width: `${progressPct}%` }}
                                />
                              </div>
                            </div>

                            <div className="px-5 py-4">
                              <div className="space-y-2.5">
                                {phases.map((p, i) => {
                                  const Icon = p.icon;
                                  const isDone = i < activeIdx;
                                  const isActive = i === activeIdx;
                                  const isPending = i > activeIdx;
                                  return (
                                    <div
                                      key={p.id}
                                      className={`flex items-start gap-3 transition-opacity duration-300 ${
                                        isPending ? 'opacity-40' : 'opacity-100'
                                      }`}
                                    >
                                      <div className={`mt-0.5 h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                                        isDone ? 'bg-green-100 text-green-600'
                                        : isActive ? 'bg-orange-100 text-orange-600'
                                        : 'bg-neutral-100 text-neutral-400'
                                      }`}>
                                        {isDone ? <CheckCircle2 className="h-4 w-4" />
                                          : isActive ? <Icon className="h-3.5 w-3.5 animate-pulse" />
                                          : <Icon className="h-3.5 w-3.5" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className={`text-xs font-semibold ${
                                          isDone ? 'text-green-700'
                                          : isActive ? 'text-orange-900'
                                          : 'text-neutral-500'
                                        }`}>
                                          {p.label}
                                          {isActive && (
                                            <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-normal text-orange-600">
                                              <span className="w-1 h-1 rounded-full bg-orange-500 animate-pulse" />
                                              in progress
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[11px] text-neutral-500 mt-0.5">{p.hint}</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="px-5 py-3 bg-neutral-50/80 border-t border-neutral-100 flex items-center gap-2">
                              <Loader2 className="h-3 w-3 text-neutral-400 animate-spin" />
                              <p className="text-[11px] text-neutral-600">
                                Typically 1–2 minutes per company. You can navigate away — processing continues in the background.
                                {queueCounts.pending > 0 && ` ${queueCounts.pending} more in queue.`}
                              </p>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Company queue */}
                      {indiaQueue.length > 0 && (
                        <div className="space-y-3">
                          {/* Summary bar */}
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-neutral-700">
                              Research Queue ({indiaQueue.length} {indiaQueue.length === 1 ? 'company' : 'companies'})
                            </h3>
                            <div className="flex items-center gap-3 text-xs">
                              {queueCounts.pending > 0 && (
                                <span className="flex items-center gap-1 text-neutral-500">
                                  <div className="w-2 h-2 rounded-full bg-neutral-400" />
                                  {queueCounts.pending} pending
                                </span>
                              )}
                              {queueCounts.processing > 0 && (
                                <span className="flex items-center gap-1 text-blue-600">
                                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                                  {queueCounts.processing} processing
                                </span>
                              )}
                              {queueCounts.completed > 0 && (
                                <span className="flex items-center gap-1 text-green-600">
                                  <div className="w-2 h-2 rounded-full bg-green-500" />
                                  {queueCounts.completed} done
                                </span>
                              )}
                              {queueCounts.failed > 0 && (
                                <span className="flex items-center gap-1 text-red-600">
                                  <div className="w-2 h-2 rounded-full bg-red-500" />
                                  {queueCounts.failed} failed
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Queue items */}
                          <div className="space-y-2">
                            {indiaQueue.map(item => (
                              <div
                                key={item.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                  item.status === 'completed' ? 'bg-green-50 border-green-200'
                                  : item.status === 'failed' ? 'bg-red-50 border-red-200'
                                  : item.status === 'processing' ? 'bg-blue-50 border-blue-200'
                                  : 'bg-neutral-50 border-neutral-200'
                                }`}
                              >
                                {/* Status icon */}
                                <div className="flex-shrink-0">
                                  {item.status === 'completed' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                                  {item.status === 'failed' && <Circle className="h-5 w-5 text-red-500" />}
                                  {item.status === 'processing' && <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />}
                                  {item.status === 'pending' && <Circle className="h-5 w-5 text-neutral-400" />}
                                </div>

                                {/* Company info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-neutral-900 text-sm truncate">{item.companyName}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium capitalize ${
                                      item.dataType === 'consolidated'
                                        ? 'bg-orange-100 text-orange-700'
                                        : 'bg-purple-100 text-purple-700'
                                    }`}>
                                      {item.dataType}
                                    </span>
                                  </div>
                                  {item.message && (
                                    <p className={`text-xs mt-0.5 ${
                                      item.status === 'completed' ? 'text-green-700'
                                      : item.status === 'failed' ? 'text-red-700'
                                      : 'text-blue-700'
                                    }`}>
                                      {item.message}
                                    </p>
                                  )}
                                  {item.status === 'processing' && !item.message && (
                                    <p className="text-xs text-blue-700 mt-0.5">Extracting financial data... (1–3 min)</p>
                                  )}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {item.status === 'failed' && (
                                    <button
                                      onClick={() => handleRetryItem(item)}
                                      disabled={isIndiaProcessing}
                                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                      aria-label={`Retry ${item.companyName}`}
                                    >
                                      <RotateCcw className="h-3 w-3" />
                                      Retry
                                    </button>
                                  )}
                                  {(item.status === 'pending' || item.status === 'failed') && (
                                    <button
                                      onClick={() => handleRemoveFromQueue(item.id)}
                                      disabled={isIndiaProcessing}
                                      className="text-neutral-400 hover:text-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed p-1"
                                      aria-label={`Remove ${item.companyName}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setIndiaResearchMode(null)}
                          disabled={isIndiaProcessing}
                        >
                          ← Back
                        </Button>
                        {indiaQueue.length > 0 && (
                          <Button
                            variant="primary"
                            className="flex-1"
                            onClick={handleStartIndiaResearch}
                            disabled={!hasPendingInQueue || isIndiaProcessing}
                          >
                            {isIndiaProcessing ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                            ) : (
                              <>
                                Start Research
                                {queueCounts.pending > 0 && ` (${queueCounts.pending} ${queueCounts.pending === 1 ? 'company' : 'companies'})`}
                                <ArrowRight className="h-4 w-4 ml-2" />
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* India Step 2b: PDF upload (shared finance-pdf panel) */}
                  {indiaResearchMode === 'pdf' && (
                    <PdfStatementsPanel
                      country="INDIA"
                      onComplete={handlePdfPanelComplete}
                      onCancel={() => setIndiaResearchMode(null)}
                      onRunningChange={handlePdfRunningChange}
                    />
                  )}
                </>
              )}

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* UK FLOW                                                     */}
              {/* ═══════════════════════════════════════════════════════════ */}
              {selectedCountry === 'uk' && (
                <>
                  <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <span className="text-xl">🇬🇧</span>
                    <div className="text-sm">
                      <span className="font-semibold text-indigo-900">United Kingdom</span>
                      <span className="text-indigo-700 ml-2">— Companies House iXBRL accounts</span>
                    </div>
                    <button
                      className="ml-auto text-xs text-indigo-600 hover:text-indigo-800 underline disabled:opacity-40"
                      onClick={() => setSelectedCountry(null)}
                      disabled={ukIsProcessing}
                    >
                      Change
                    </button>
                  </div>

                  <div className="space-y-5">
                    <h2 className="text-xl font-semibold text-neutral-900">Search Companies House</h2>

                    {/* Single-company form: Name (with autocomplete) + CH Number + Time Range + Add to Queue */}
                    <Card>
                      <CardBody className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                            Company Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
                            <input
                              type="text"
                              value={ukCompanyName}
                              onChange={e => {
                                setUkCompanyName(e.target.value);
                                // Typing again invalidates a prior pick — re-enable search
                                // and drop the auto-filled CH number so the user can re-pick.
                                if (ukSearchSuppressed) {
                                  setUkSearchSuppressed(false);
                                  setUkCompanyNumber('');
                                }
                              }}
                              onFocus={() => {
                                if (!ukSearchSuppressed && ukCompanyName.trim().length >= 2 && ukSearchResults.length > 0) {
                                  setUkSearchOpen(true);
                                }
                              }}
                              onBlur={() => {
                                // Delay so an option click registers before the menu closes.
                                setTimeout(() => setUkSearchOpen(false), 150);
                              }}
                              onKeyDown={e => { if (e.key === 'Enter' && !ukIsProcessing) handleAddUKToQueue(); }}
                              disabled={ukIsProcessing}
                              placeholder="e.g. BP PLC, Tesco PLC, AstraZeneca PLC"
                              className="w-full pl-9 pr-9 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-neutral-50 disabled:cursor-not-allowed"
                              autoComplete="off"
                            />
                            {ukSearchLoading && (
                              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 animate-spin" />
                            )}

                            {/* Autocomplete dropdown — live Companies House results */}
                            {ukSearchOpen && !ukIsProcessing && ukCompanyName.trim().length >= 2 && (
                              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg">
                                {ukSearchLoading && ukSearchResults.length === 0 && (
                                  <div className="flex items-center justify-center py-4 text-xs text-neutral-500">
                                    <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                    Searching Companies House…
                                  </div>
                                )}
                                {!ukSearchLoading && ukSearchResults.length === 0 && (
                                  <p className="py-3 text-center text-xs text-neutral-500">
                                    No companies found on Companies House
                                  </p>
                                )}
                                {ukSearchResults.map(item => (
                                  <button
                                    key={item.company_number}
                                    type="button"
                                    onMouseDown={e => e.preventDefault()} // keep input focus so onBlur doesn't race
                                    onClick={() => {
                                      setUkCompanyName(item.company_name);
                                      setUkCompanyNumber(item.company_number.toUpperCase());
                                      setUkSearchSuppressed(true);
                                      setUkSearchOpen(false);
                                      setUkSearchResults([]);
                                    }}
                                    className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-indigo-50 border-b border-neutral-100 last:border-b-0"
                                  >
                                    <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-neutral-900 truncate normal-case">
                                        {item.company_name || item.company_number}
                                      </p>
                                      <div className="flex items-center gap-2 text-[11px] text-neutral-500 mt-0.5">
                                        <span className="font-mono">#{item.company_number}</span>
                                        {item.company_status && (
                                          <span className={item.company_status === 'active' ? 'text-green-600' : 'text-red-600'}>
                                            {item.company_status}
                                          </span>
                                        )}
                                        {item.address_snippet && (
                                          <span className="truncate">{item.address_snippet}</span>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-neutral-500 mt-1">
                            Live results from Companies House — pick one to auto-fill the registration number.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                            Companies House Number <span className="text-neutral-400 font-normal">(optional)</span>
                          </label>
                          <div className="relative">
                            <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
                            <input
                              type="text"
                              value={ukCompanyNumber}
                              onChange={e => setUkCompanyNumber(e.target.value.toUpperCase())}
                              disabled={ukIsProcessing}
                              placeholder="8-digit registration number (e.g. 00102498)"
                              maxLength={8}
                              className="w-full pl-9 pr-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-neutral-50 disabled:cursor-not-allowed font-mono"
                            />
                          </div>
                          <p className="text-xs text-neutral-500 mt-1">
                            Leave blank to auto-resolve by name. Providing the number speeds up lookup and avoids ambiguous matches.
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-sm font-medium text-neutral-700">Time Range</label>
                            {ukAvailabilityLoading ? (
                              <span className="flex items-center gap-1 text-xs text-neutral-500">
                                <Loader2 className="h-3 w-3 animate-spin" /> Checking availability...
                              </span>
                            ) : ukAvailability?.status === 'success' && ukAvailability.filing_count > 0 ? (
                              <span className="flex items-center gap-1.5 text-xs text-green-700">
                                <CheckCircle2 className="h-3 w-3" />
                                {ukAvailability.filing_count} year{ukAvailability.filing_count === 1 ? '' : 's'} available
                                {ukAvailability.available_years.length > 0 && (
                                  <span className="text-neutral-500 font-normal">
                                    ({ukAvailability.available_years[ukAvailability.available_years.length - 1]}–{ukAvailability.available_years[0]})
                                  </span>
                                )}
                              </span>
                            ) : ukAvailability?.status === 'not_found' ? (
                              <span className="flex items-center gap-1.5 text-xs text-amber-600">
                                <Circle className="h-3 w-3" />
                                Company not found on Companies House
                              </span>
                            ) : null}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                            {(['1year', '2years', '3years', '5years', '10years'] as UKTimeRange[]).map(tr => {
                              const yearsRequired = parseInt(tr.replace('year', '').replace('s', ''), 10);
                              const filingCount = ukAvailability?.status === 'success' ? ukAvailability.filing_count : null;
                              const exceedsAvailable = filingCount !== null && yearsRequired > filingCount;
                              const disabled = ukIsProcessing || exceedsAvailable;
                              return (
                                <button
                                  key={tr}
                                  onClick={() => !exceedsAvailable && setUkTimeRange(tr)}
                                  disabled={disabled}
                                  title={exceedsAvailable ? `Only ${filingCount} year${filingCount === 1 ? '' : 's'} of data available on Companies House` : undefined}
                                  className={`py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                                    ukTimeRange === tr
                                      ? 'border-indigo-500 bg-indigo-100 text-indigo-700'
                                      : exceedsAvailable
                                        ? 'border-neutral-200 bg-neutral-50 text-neutral-300 line-through'
                                        : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                                  } disabled:cursor-not-allowed`}
                                >
                                  {tr === '1year' ? '1 Year' : tr.replace('years', ' Years')}
                                </button>
                              );
                            })}
                          </div>
                          {ukAvailability?.status === 'success' && ukAvailability.filing_count > 0 && ukAvailability.filing_count < 10 && (
                            <p className="text-xs text-neutral-500 mt-1.5">
                              Ranges longer than {ukAvailability.filing_count} year{ukAvailability.filing_count === 1 ? '' : 's'} are disabled because Companies House does not have that much filing history for this company.
                            </p>
                          )}
                        </div>

                        <Button
                          variant="primary"
                          className="w-full"
                          onClick={handleAddUKToQueue}
                          disabled={ukIsProcessing || ukCompanyName.trim().length === 0}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add to Research Queue
                        </Button>
                      </CardBody>
                    </Card>

                    {/* Rich extraction loader — only when ONE company is processing.
                        When multiple run concurrently the elaborate phase stepper is
                        ambiguous, so we fall back to the per-row status in the queue
                        list below. */}
                    {ukQueueCounts.processing === 1 && (() => {
                      const activeItem = ukQueue.find(i => i.status === 'processing');
                      if (!activeItem) return null;

                      const phases = [
                        { id: 'resolve',   label: 'Resolving company',           hint: 'Looking up Companies House registry',           icon: Building2,  startSec: 0   },
                        { id: 'download',  label: 'Downloading filings',         hint: 'Pulling annual accounts (iXBRL / XBRL / ZIP)',  icon: Download,   startSec: 5   },
                        { id: 'parse',     label: 'Parsing iXBRL',               hint: 'Extracting tagged financial line items',        icon: FileSearch, startSec: 30  },
                        { id: 'normalize', label: 'Normalising fields',          hint: 'Mapping to unified schema · GBP Millions',     icon: Sparkles,   startSec: 75  },
                        { id: 'save',      label: 'Saving to graph',             hint: 'Persisting company + year nodes to Neo4j',      icon: Database,   startSec: 130 },
                      ];
                      const activeIdx = phases.reduce(
                        (idx, p, i) => (ukElapsedSec >= p.startSec ? i : idx),
                        0
                      );
                      const mins = Math.floor(ukElapsedSec / 60);
                      const secs = ukElapsedSec % 60;
                      const elapsedLabel = mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;

                      const requestedYears = parseInt(activeItem.timeRange.replace('year', '').replace('s', ''), 10) || 5;
                      const availableYears = ukAvailability?.status === 'success' ? ukAvailability.filing_count : null;
                      const skeletonCount = Math.min(requestedYears, availableYears ?? requestedYears);
                      const currentYear = new Date().getFullYear();
                      const yearLabels = Array.from({ length: skeletonCount }, (_, i) => currentYear - 1 - i);

                      const progressPct = Math.min(95, Math.round((ukElapsedSec / 180) * 95));

                      return (
                        <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-indigo-50/40 overflow-hidden shadow-sm">
                          <div className="px-5 py-4 border-b border-indigo-100 bg-white/60 backdrop-blur">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <div className="relative h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                  <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
                                  <div className="absolute inset-0 rounded-full ring-4 ring-indigo-200/50 animate-ping" />
                                </div>
                                <div>
                                  <h3 className="text-sm font-semibold text-indigo-900">Fetching from Companies House</h3>
                                  <p className="text-xs text-indigo-700 mt-0.5 normal-case">
                                    {activeItem.companyName}
                                    {activeItem.companyNumber && (
                                      <span className="text-neutral-500 ml-1.5 font-mono">#{activeItem.companyNumber}</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-[11px] uppercase tracking-wide text-neutral-500">Elapsed</div>
                                <div className="text-sm font-mono font-semibold text-indigo-900 tabular-nums">{elapsedLabel}</div>
                              </div>
                            </div>

                            <div className="mt-3 w-full bg-indigo-100 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-full rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                          </div>

                          <div className="px-5 py-4">
                            <div className="space-y-2.5">
                              {phases.map((p, i) => {
                                const Icon = p.icon;
                                const isDone = i < activeIdx;
                                const isActive = i === activeIdx;
                                const isPending = i > activeIdx;
                                return (
                                  <div
                                    key={p.id}
                                    className={`flex items-start gap-3 transition-opacity duration-300 ${
                                      isPending ? 'opacity-40' : 'opacity-100'
                                    }`}
                                  >
                                    <div className={`mt-0.5 h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                                      isDone ? 'bg-green-100 text-green-600'
                                      : isActive ? 'bg-indigo-100 text-indigo-600'
                                      : 'bg-neutral-100 text-neutral-400'
                                    }`}>
                                      {isDone ? <CheckCircle2 className="h-4 w-4" />
                                        : isActive ? <Icon className="h-3.5 w-3.5 animate-pulse" />
                                        : <Icon className="h-3.5 w-3.5" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className={`text-xs font-semibold ${
                                        isDone ? 'text-green-700'
                                        : isActive ? 'text-indigo-900'
                                        : 'text-neutral-500'
                                      }`}>
                                        {p.label}
                                        {isActive && (
                                          <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-normal text-indigo-600">
                                            <span className="w-1 h-1 rounded-full bg-indigo-500 animate-pulse" />
                                            in progress
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-[11px] text-neutral-500 mt-0.5">{p.hint}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {skeletonCount > 0 && (
                            <div className="px-5 pb-4">
                              <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-2 flex items-center gap-1.5">
                                <Calendar className="h-3 w-3" />
                                Extracting {skeletonCount} year{skeletonCount === 1 ? '' : 's'} of accounts
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                {yearLabels.map((yr, idx) => (
                                  <div
                                    key={yr}
                                    className="relative rounded-lg border border-indigo-100 bg-white p-2.5 overflow-hidden"
                                    style={{ animationDelay: `${idx * 150}ms` }}
                                  >
                                    <div className="text-[10px] text-neutral-400 font-mono mb-1.5">FY {yr}</div>
                                    <div className="h-1.5 bg-neutral-100 rounded animate-pulse w-3/4 mb-1" />
                                    <div className="h-1.5 bg-neutral-100 rounded animate-pulse w-1/2" />
                                    <div
                                      className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-indigo-100/40 to-transparent"
                                      style={{ animation: `shimmer 2s ease-in-out ${idx * 0.15}s infinite` }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="px-5 py-3 bg-neutral-50/80 border-t border-neutral-100 flex items-center gap-2">
                            <Loader2 className="h-3 w-3 text-neutral-400 animate-spin" />
                            <p className="text-[11px] text-neutral-600">
                              Typically 1–3 minutes per company. You can navigate away — processing continues in the background.
                              {ukQueueCounts.pending > 0 && ` ${ukQueueCounts.pending} more in queue.`}
                            </p>
                          </div>

                          <style>{`
                            @keyframes shimmer {
                              0% { transform: translateX(-100%); }
                              100% { transform: translateX(100%); }
                            }
                          `}</style>
                        </div>
                      );
                    })()}

                    {/* Research queue (mirrors India's queue UX) */}
                    {ukQueue.length > 0 && (
                      <div className="space-y-3">
                        {/* Summary bar */}
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-neutral-700">
                            Research Queue ({ukQueue.length} {ukQueue.length === 1 ? 'company' : 'companies'})
                          </h3>
                          <div className="flex items-center gap-3 text-xs">
                            {ukQueueCounts.pending > 0 && (
                              <span className="flex items-center gap-1 text-neutral-500">
                                <div className="w-2 h-2 rounded-full bg-neutral-400" />
                                {ukQueueCounts.pending} pending
                              </span>
                            )}
                            {ukQueueCounts.processing > 0 && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                                {ukQueueCounts.processing} processing
                              </span>
                            )}
                            {ukQueueCounts.completed > 0 && (
                              <span className="flex items-center gap-1 text-green-600">
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                {ukQueueCounts.completed} done
                              </span>
                            )}
                            {ukQueueCounts.failed > 0 && (
                              <span className="flex items-center gap-1 text-red-600">
                                <div className="w-2 h-2 rounded-full bg-red-500" />
                                {ukQueueCounts.failed} failed
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Queue items */}
                        <div className="space-y-2">
                          {ukQueue.map(item => (
                            <div
                              key={item.id}
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                item.status === 'completed' ? 'bg-green-50 border-green-200'
                                : item.status === 'failed' ? 'bg-red-50 border-red-200'
                                : item.status === 'processing' ? 'bg-blue-50 border-blue-200'
                                : 'bg-neutral-50 border-neutral-200'
                              }`}
                            >
                              {/* Status icon */}
                              <div className="flex-shrink-0">
                                {item.status === 'completed' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                                {item.status === 'failed' && <Circle className="h-5 w-5 text-red-500" />}
                                {item.status === 'processing' && <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />}
                                {item.status === 'pending' && <Circle className="h-5 w-5 text-neutral-400" />}
                              </div>

                              {/* Company info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-neutral-900 text-sm truncate">{item.companyName}</span>
                                  {item.companyNumber && (
                                    <span className="font-mono text-xs text-neutral-500">#{item.companyNumber}</span>
                                  )}
                                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                    {item.timeRange === '1year' ? '1Y' : item.timeRange.replace('years', 'Y')}
                                  </span>
                                </div>
                                {item.message && (
                                  <p className={`text-xs mt-0.5 ${
                                    item.status === 'completed' ? 'text-green-700'
                                    : item.status === 'failed' ? 'text-red-700'
                                    : 'text-blue-700'
                                  }`}>
                                    {item.message}
                                  </p>
                                )}
                                {item.status === 'processing' && !item.message && (
                                  <p className="text-xs text-blue-700 mt-0.5">Fetching from Companies House… (1–3 min)</p>
                                )}
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {item.status === 'failed' && (
                                  <button
                                    onClick={() => handleRetryUKItem(item)}
                                    disabled={ukIsProcessing}
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    aria-label={`Retry ${item.companyName}`}
                                  >
                                    <RotateCcw className="h-3 w-3" />
                                    Retry
                                  </button>
                                )}
                                {(item.status === 'pending' || item.status === 'failed') && (
                                  <button
                                    onClick={() => handleRemoveUKFromQueue(item.id)}
                                    disabled={ukIsProcessing}
                                    className="text-neutral-400 hover:text-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed p-1"
                                    aria-label={`Remove ${item.companyName}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action buttons — back + start-the-queue */}
                    <div className="flex gap-3 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedCountry(null)}
                        disabled={ukIsProcessing}
                      >
                        ← Back
                      </Button>
                      {ukQueue.length > 0 && (
                        <Button
                          variant="primary"
                          className="flex-1"
                          onClick={handleStartUKResearch}
                          disabled={!hasPendingInUKQueue || ukIsProcessing}
                        >
                          {ukIsProcessing ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fetching from Companies House...</>
                          ) : (
                            <>
                              Start UK Research
                              {ukQueueCounts.pending > 0 && ` (${ukQueueCounts.pending} ${ukQueueCounts.pending === 1 ? 'company' : 'companies'})`}
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* US FLOW                                                     */}
              {/* ═══════════════════════════════════════════════════════════ */}
              {selectedCountry === 'us' && (
                <>
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-xl">🇺🇸</span>
                    <div className="text-sm">
                      <span className="font-semibold text-blue-900">United States</span>
                      <span className="text-blue-700 ml-2">— SEC EDGAR / PDF upload</span>
                    </div>
                    <button
                      className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-40"
                      onClick={() => setSelectedCountry(null)}
                      disabled={isPdfPanelRunning}
                    >
                      Change
                    </button>
                  </div>

                  {/* Step 1: Mode selection (kept — SEC vs PDF is fundamentally different) */}
                  {researchMode === null && (
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-xl font-semibold text-neutral-900 mb-2">Select Research Source</h2>
                        <p className="text-neutral-600">Choose how you want to research this company</p>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <Card
                          className="cursor-pointer transition-all hover:shadow-lg"
                          onClick={() => setResearchMode('sec')}
                        >
                          <CardBody className="text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-primary-100 mx-auto flex items-center justify-center mb-4">
                              <FileText className="h-8 w-8 text-primary-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-2">SEC Public Company</h3>
                            <p className="text-sm text-neutral-600 mb-4">Auto-fetch 10-K/10-Q filings from SEC EDGAR</p>
                            <div className="space-y-2 text-sm text-neutral-700">
                              {['Auto-fetch filings', 'Historical data (1-5 years)', 'XBRL structured data', 'Add multiple companies at once'].map(f => (
                                <div key={f} className="flex items-center justify-center gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" /><span>{f}</span>
                                </div>
                              ))}
                            </div>
                          </CardBody>
                        </Card>

                        {/* PDF Upload — the finance-pdf pipeline resolves the target
                            company from the document itself (no companyId parameter),
                            so a manually picked company can't be honored in
                            "Add to Existing" mode. */}
                        <Card
                          className={companySelectionMode === 'existing'
                            ? 'opacity-60 cursor-not-allowed'
                            : 'cursor-pointer transition-all hover:shadow-lg'}
                          onClick={companySelectionMode === 'existing' ? undefined : () => setResearchMode('pdf')}
                        >
                          <CardBody className="text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-secondary-100 mx-auto flex items-center justify-center mb-4">
                              <Upload className="h-8 w-8 text-secondary-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Private Company (PDF)</h3>
                            <p className="text-sm text-neutral-600 mb-4">Upload annual reports or financial statements</p>
                            <div className="space-y-2 text-sm text-neutral-700">
                              {[['Upload annual report PDF', 'amber'], ['AI-powered extraction', 'amber'], ['Company auto-detected from the PDF', 'green']].map(([f, c]) => (
                                <div key={f} className="flex items-center justify-center gap-2">
                                  <div className={`w-1.5 h-1.5 rounded-full bg-${c}-500`} /><span>{f}</span>
                                </div>
                              ))}
                            </div>
                            {companySelectionMode === 'existing' && (
                              <p className="text-xs text-neutral-500 mt-4">
                                The company is identified from the PDF itself — switch to
                                &ldquo;Create New Company&rdquo; to upload. Reports for an
                                existing company are matched to it automatically.
                              </p>
                            )}
                          </CardBody>
                        </Card>
                      </div>
                    </div>
                  )}

                  {/* SEC mode: single-company autocomplete form + queue */}
                  {researchMode === 'sec' && (
                    <div className="space-y-5">
                      <h2 className="text-xl font-semibold text-neutral-900">Search SEC EDGAR</h2>

                      <Card>
                        <CardBody className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                              Company Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
                              <input
                                type="text"
                                value={usCompanyName}
                                onChange={e => {
                                  setUsCompanyName(e.target.value);
                                  if (usSearchSuppressed) {
                                    setUsSearchSuppressed(false);
                                    setUsCompanyTicker('');
                                    setUsCompanyCik('');
                                  }
                                }}
                                onFocus={() => {
                                  if (!usSearchSuppressed && usCompanyName.trim().length >= 4 && searchResults.length > 0) {
                                    setUsSearchOpen(true);
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(() => setUsSearchOpen(false), 150);
                                }}
                                onKeyDown={e => { if (e.key === 'Enter' && !isUSProcessing) handleAddUSToQueue(); }}
                                disabled={isUSProcessing}
                                placeholder="e.g. Apple Inc., Microsoft, Tesla"
                                className="w-full pl-9 pr-9 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-50 disabled:cursor-not-allowed"
                                autoComplete="off"
                              />
                              {isSearching && (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 animate-spin" />
                              )}

                              {usSearchOpen && !isUSProcessing && usCompanyName.trim().length >= 4 && (
                                <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg">
                                  {isSearching && searchResults.length === 0 && (
                                    <div className="flex items-center justify-center py-4 text-xs text-neutral-500">
                                      <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                      Searching SEC EDGAR…
                                    </div>
                                  )}
                                  {!isSearching && searchResults.length === 0 && (
                                    <p className="py-3 text-center text-xs text-neutral-500">No companies found</p>
                                  )}
                                  {searchResults.map(item => (
                                    <button
                                      key={item.cik}
                                      type="button"
                                      onMouseDown={e => e.preventDefault()}
                                      onClick={() => {
                                        setUsCompanyName(item.companyName);
                                        setUsCompanyTicker(item.ticker || '');
                                        setUsCompanyCik(item.cik || '');
                                        setUsSearchSuppressed(true);
                                        setUsSearchOpen(false);
                                        setSearchResults([]);
                                      }}
                                      className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-blue-50 border-b border-neutral-100 last:border-b-0"
                                    >
                                      <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-neutral-900 truncate">
                                          {item.companyName}
                                        </p>
                                        <div className="flex items-center gap-2 text-[11px] text-neutral-500 mt-0.5">
                                          <span className="font-medium text-blue-600">{item.ticker}</span>
                                          <span className="font-mono">CIK: {item.cik}</span>
                                          {item.exchange && <span>{item.exchange}</span>}
                                        </div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-neutral-500 mt-1">
                              Live results from SEC EDGAR — pick one to auto-fill the ticker and CIK.
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                              Ticker <span className="text-neutral-400 font-normal">(optional)</span>
                            </label>
                            <div className="relative">
                              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
                              <input
                                type="text"
                                value={usCompanyTicker}
                                onChange={e => setUsCompanyTicker(e.target.value.toUpperCase())}
                                disabled={isUSProcessing}
                                placeholder="e.g. AAPL, MSFT, TSLA"
                                className="w-full pl-9 pr-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-50 disabled:cursor-not-allowed font-mono"
                              />
                            </div>
                            <p className="text-xs text-neutral-500 mt-1">
                              Leave blank to auto-resolve by name. Providing the ticker speeds up lookup.
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Time Range</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {(['1year', '2years', '3years', '5years'] as TimeRange[]).map(tr => (
                                <button
                                  key={tr}
                                  onClick={() => setTimeRange(tr)}
                                  disabled={isUSProcessing}
                                  className={`py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                                    timeRange === tr
                                      ? 'border-blue-500 bg-blue-100 text-blue-700'
                                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                                  } disabled:cursor-not-allowed`}
                                >
                                  {tr === '1year' ? '1 Year' : tr.replace('years', ' Years')}
                                </button>
                              ))}
                            </div>
                          </div>

                          <Button
                            variant="primary"
                            className="w-full"
                            onClick={handleAddUSToQueue}
                            disabled={isUSProcessing || usCompanyName.trim().length === 0}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add to Research Queue
                          </Button>
                        </CardBody>
                      </Card>

                      {/* Rich extraction loader — only when ONE company is processing.
                          When multiple run concurrently the elaborate phase stepper is
                          ambiguous, so we fall back to the per-row status in the queue
                          list below. */}
                      {usQueueCounts.processing === 1 && (() => {
                        const activeItem = usQueue.find(i => i.status === 'processing');
                        if (!activeItem) return null;

                        const phases = [
                          { id: 'resolve',   label: 'Resolving company',     hint: 'Looking up SEC ticker / CIK',                       icon: Building2,  startSec: 0   },
                          { id: 'fetch',     label: 'Fetching XBRL filings', hint: 'Pulling 10-K / 10-Q reports from SEC EDGAR',         icon: Download,   startSec: 8   },
                          { id: 'parse',     label: 'Parsing facts',         hint: 'Extracting structured financial line items',         icon: FileSearch, startSec: 30  },
                          { id: 'calc',      label: 'Calculating metrics',   hint: 'WACC, ROIC, EV, CAP, NOPAT, CAGR',                   icon: Sparkles,   startSec: 60  },
                          { id: 'save',      label: 'Saving to graph',       hint: 'Persisting company + year nodes to Neo4j',           icon: Database,   startSec: 90  },
                        ];
                        const activeIdx = phases.reduce(
                          (idx, p, i) => (usElapsedSec >= p.startSec ? i : idx),
                          0
                        );
                        const mins = Math.floor(usElapsedSec / 60);
                        const secs = usElapsedSec % 60;
                        const elapsedLabel = mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
                        const progressPct = Math.min(95, Math.round((usElapsedSec / 120) * 95));

                        return (
                          <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50/40 overflow-hidden shadow-sm">
                            <div className="px-5 py-4 border-b border-blue-100 bg-white/60 backdrop-blur">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                  <div className="relative h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                                    <div className="absolute inset-0 rounded-full ring-4 ring-blue-200/50 animate-ping" />
                                  </div>
                                  <div>
                                    <h3 className="text-sm font-semibold text-blue-900">Fetching from SEC EDGAR</h3>
                                    <p className="text-xs text-blue-700 mt-0.5">
                                      {activeItem.companyName}
                                      {activeItem.ticker && (
                                        <span className="text-neutral-500 ml-1.5 font-mono">{activeItem.ticker}</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="text-[11px] uppercase tracking-wide text-neutral-500">Elapsed</div>
                                  <div className="text-sm font-mono font-semibold text-blue-900 tabular-nums">{elapsedLabel}</div>
                                </div>
                              </div>
                              <div className="mt-3 w-full bg-blue-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-1000 ease-out"
                                  style={{ width: `${progressPct}%` }}
                                />
                              </div>
                            </div>

                            <div className="px-5 py-4">
                              <div className="space-y-2.5">
                                {phases.map((p, i) => {
                                  const Icon = p.icon;
                                  const isDone = i < activeIdx;
                                  const isActive = i === activeIdx;
                                  const isPending = i > activeIdx;
                                  return (
                                    <div
                                      key={p.id}
                                      className={`flex items-start gap-3 transition-opacity duration-300 ${
                                        isPending ? 'opacity-40' : 'opacity-100'
                                      }`}
                                    >
                                      <div className={`mt-0.5 h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                                        isDone ? 'bg-green-100 text-green-600'
                                        : isActive ? 'bg-blue-100 text-blue-600'
                                        : 'bg-neutral-100 text-neutral-400'
                                      }`}>
                                        {isDone ? <CheckCircle2 className="h-4 w-4" />
                                          : isActive ? <Icon className="h-3.5 w-3.5 animate-pulse" />
                                          : <Icon className="h-3.5 w-3.5" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className={`text-xs font-semibold ${
                                          isDone ? 'text-green-700'
                                          : isActive ? 'text-blue-900'
                                          : 'text-neutral-500'
                                        }`}>
                                          {p.label}
                                          {isActive && (
                                            <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-normal text-blue-600">
                                              <span className="w-1 h-1 rounded-full bg-blue-500 animate-pulse" />
                                              in progress
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[11px] text-neutral-500 mt-0.5">{p.hint}</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="px-5 py-3 bg-neutral-50/80 border-t border-neutral-100 flex items-center gap-2">
                              <Loader2 className="h-3 w-3 text-neutral-400 animate-spin" />
                              <p className="text-[11px] text-neutral-600">
                                Typically 2–3 minutes per company. You can navigate away — processing continues in the background.
                                {usQueueCounts.pending > 0 && ` ${usQueueCounts.pending} more in queue.`}
                              </p>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Research queue (mirrors UK/India) */}
                      {usQueue.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-neutral-700">
                              Research Queue ({usQueue.length} {usQueue.length === 1 ? 'company' : 'companies'})
                            </h3>
                            <div className="flex items-center gap-3 text-xs">
                              {usQueueCounts.pending > 0 && (
                                <span className="flex items-center gap-1 text-neutral-500">
                                  <div className="w-2 h-2 rounded-full bg-neutral-400" />
                                  {usQueueCounts.pending} pending
                                </span>
                              )}
                              {usQueueCounts.processing > 0 && (
                                <span className="flex items-center gap-1 text-blue-600">
                                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                                  {usQueueCounts.processing} processing
                                </span>
                              )}
                              {usQueueCounts.completed > 0 && (
                                <span className="flex items-center gap-1 text-green-600">
                                  <div className="w-2 h-2 rounded-full bg-green-500" />
                                  {usQueueCounts.completed} done
                                </span>
                              )}
                              {usQueueCounts.failed > 0 && (
                                <span className="flex items-center gap-1 text-red-600">
                                  <div className="w-2 h-2 rounded-full bg-red-500" />
                                  {usQueueCounts.failed} failed
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="space-y-2">
                            {usQueue.map(item => (
                              <div
                                key={item.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                  item.status === 'completed' ? 'bg-green-50 border-green-200'
                                  : item.status === 'failed' ? 'bg-red-50 border-red-200'
                                  : item.status === 'processing' ? 'bg-blue-50 border-blue-200'
                                  : 'bg-neutral-50 border-neutral-200'
                                }`}
                              >
                                <div className="flex-shrink-0">
                                  {item.status === 'completed' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                                  {item.status === 'failed' && <Circle className="h-5 w-5 text-red-500" />}
                                  {item.status === 'processing' && <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />}
                                  {item.status === 'pending' && <Circle className="h-5 w-5 text-neutral-400" />}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-neutral-900 text-sm truncate">{item.companyName}</span>
                                    {item.ticker && (
                                      <span className="font-mono text-xs text-blue-600">{item.ticker}</span>
                                    )}
                                    <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                      {item.timeRange === '1year' ? '1Y' : item.timeRange.replace('years', 'Y')}
                                    </span>
                                  </div>
                                  {item.message && (
                                    <p className={`text-xs mt-0.5 ${
                                      item.status === 'completed' ? 'text-green-700'
                                      : item.status === 'failed' ? 'text-red-700'
                                      : 'text-blue-700'
                                    }`}>
                                      {item.message}
                                    </p>
                                  )}
                                  {item.status === 'processing' && !item.message && (
                                    <p className="text-xs text-blue-700 mt-0.5">Fetching from SEC EDGAR… (2–3 min)</p>
                                  )}
                                </div>

                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {item.status === 'failed' && (
                                    <button
                                      onClick={() => handleRetryUSItem(item)}
                                      disabled={isUSProcessing}
                                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                      aria-label={`Retry ${item.companyName}`}
                                    >
                                      <RotateCcw className="h-3 w-3" />
                                      Retry
                                    </button>
                                  )}
                                  {(item.status === 'pending' || item.status === 'failed') && (
                                    <button
                                      onClick={() => handleRemoveUSFromQueue(item.id)}
                                      disabled={isUSProcessing}
                                      className="text-neutral-400 hover:text-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed p-1"
                                      aria-label={`Remove ${item.companyName}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setResearchMode(null)}
                          disabled={isUSProcessing}
                        >
                          ← Back
                        </Button>
                        {usQueue.length > 0 && (
                          <Button
                            variant="primary"
                            className="flex-1"
                            onClick={handleStartUSResearch}
                            disabled={!hasPendingInUSQueue || isUSProcessing}
                          >
                            {isUSProcessing ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fetching from SEC EDGAR...</>
                            ) : (
                              <>
                                Start US Research
                                {usQueueCounts.pending > 0 && ` (${usQueueCounts.pending} ${usQueueCounts.pending === 1 ? 'company' : 'companies'})`}
                                <ArrowRight className="h-4 w-4 ml-2" />
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* PDF mode: shared finance-pdf panel */}
                  {researchMode === 'pdf' && (
                    <PdfStatementsPanel
                      country="US"
                      onComplete={handlePdfPanelComplete}
                      onCancel={() => setResearchMode(null)}
                      onRunningChange={handlePdfRunningChange}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
